/**
 * Plan editor — trace, draw and inventory in one pass (spec 2026-08-22-02).
 *
 * Route: `/$org/assets/$assetUid/plan-editor`, valid for `floor` and `unit`
 * assets. The page loads `GET /assets/{uid}/plan/scene`, hands the resulting
 * document to the imperative engine (`engine/tools.ts`, which owns the SVG DOM
 * and every pointer interaction), and keeps only the coarse shell in React:
 * toolbar, layer panel, inspector, validation, save.
 *
 * Drawing **is** inventorying: closing a room polygon creates the room asset,
 * placing a radiator creates the equipment asset. Save computes a diff against
 * the loaded scene and replays it in dependency order — and deleting an element
 * never deletes its asset, only the representation that drew it.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useBlocker, useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import { mdiAlertCircleOutline, mdiCheckCircleOutline, mdiFloorPlan, mdiLoading } from '@mdi/js'

import { $api } from '../../api'
import { apiBaseUrl } from '../../api/client'
import { useApi } from '../../api/hooks'
import { NETWORK_SEGMENTS_PATH } from '../plan/NetworkAssemblyControl'
import { useFeatureFlag } from '../../hooks/use-feature-flags'
import { fetchAllPages, listQueryKey } from '../../api/paginateAll'
import { useAsset, useAssetTypes } from '../../hooks/useQueries'
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import { useIsMobile } from '../../hooks/use-mobile'
import { useFormatters } from '../../lib/format'
import { Button } from '../ui/button'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog'
import { Input } from '../ui/input'
import { CalibratePanel } from './CalibratePanel'
import { EditorToolbar, type DrawParent } from './EditorToolbar'
import { LayerPanel } from './LayerPanel'
import { InspectorPanel, type SharedLayer } from './InspectorPanel'
import { UncalibratedBadge } from './UncalibratedBadge'
import { ValidationPanel } from './ValidationPanel'
import { ToolOptionsPanel } from './ToolOptionsPanel'
import { Checkbox } from '../ui/checkbox'
import { apiErrorMessage, useDigitizePlan, type DigitizeOutput } from './useDigitizePlan'
import { DigitizePreviewDialog, type DigitizeKeep } from './DigitizePreviewDialog'
import { PlanProposalDialog } from './PlanProposalDialog'
import { usePlanProposals } from './usePlanProposals'
import { useCalibratePlan, type CalibrateOutput } from './useCalibratePlan'
import {
    floorplanIssuesByElement,
    hasFloorplanErrors,
    validateFloorplan,
    type FloorplanIssue,
} from './engine/validate'
import { useWallThickness } from './useWallThickness'
import { useEquipmentSizes, type EquipmentSize } from './useEquipmentSizes'
import { useRecentGroupers } from './useRecentGroupers'
import type { PickedEquipmentType } from './EquipmentTypePicker'
import { PLAN_SCENE_PATH, useSavePlan, type SaveOutcome } from './useSavePlan'
import { autoFitTransform, calibrationFromStatedScale, isUntrustedCalibration } from './engine/calibrate'
import { importedPoints, parseFloorplanV1 } from './engine/import-floorplan'
import { applyTransform, bboxOfPoints, centroid, invertTransform, type Point } from './engine/geometry'
import { LAYER_IDS, defaultLayerStates, type LayerId } from './engine/render'
import {
    assignRunNetworkAssets,
    catalogueLevel,
    effectiveLevel,
    levelParentsOf,
    networkAssetName,
    resolveRunTarget,
    runLevel,
    segmentAssembly,
    segmentsToAttach,
    type NetworkAssetRow,
    type NetworkContext,
} from './engine/network-assets'
import {
    FRAME_ANCHOR_TYPE_CODES,
    implicitFrameUid,
    needsExplicitRegistration,
    typeAnchorsFrame,
    possibleParentCodes,
    resolveCreatedAssetParent,
    type AssetLite,
    type AssetTypeNode,
    type ParentResolution,
} from './engine/parents'
import {
    assetFootprints,
    autoFitUnderlayTransform,
    bundleTargets,
    canPromote,
    clearCalibrationEdit,
    clearRegistrationEdit,
    cloneDoc,
    computeDiff,
    containingSpace,
    documentPoints,
    elementByKey,
    geometryPoints,
    groupByRepresentation,
    hydrateOwnerNames,
    isEmptyDiff,
    isGrouperElement,
    isGrouperTypeCode,
    AUTO_NAME_FLAG,
    ROOM_NUMBER_PROPERTY,
    NETWORK_KIND_PROPERTY,
    UNIT_KIND_PROPERTY,
    nextKey,
    ownerRoomNumber,
    ownerTypeCode,
    overrideExistingParent,
    overridePendingParent,
    overridePendingParentKey,
    rasterAspect,
    repAssetUidOf,
    sanitizeNetworks,
    sceneFromPlanScene,
    sceneLayers,
    sceneMmPerUnit,
    type EditorElement,
    type ElementOwner,
    type GrouperRef,
    type NetworkLevel,
    type RiserPassage,
    type RunElement,
    type SceneDoc,
} from './engine/scene'
import { CREATION_TOOLS, PlanEditor, type CalibrationState, type ClipboardItem, type PlanViewport, type ToolId } from './engine/tools'
import type { ValidationIssue } from './engine/validation'
import './plan-editor.css'
import { lotBaseTypeCode } from '../../lib/asset-type-tree'

/** Asset types the editor can anchor a frame on (spec 2026-08-22-01 §2). */
const EDITABLE_TYPE_CODES = ['floor', 'unit']
const DEFAULT_DOOR_TYPE = 'openings_interior_door'
const DEFAULT_WINDOW_TYPE = 'openings_window'
const ROOM_TYPE = 'room'
/** The split tool's default grouper type (spec 2026-08-30-01 §7). */
const UNIT_TYPE = 'unit'
/**
 * What a drawn lot is created as: housing, the kind the inspector shows by
 * default, and the only lot subtype that accepts the rooms drawn inside it
 * (spec 2026-10-09-04).
 */
const NEW_GROUPER_TYPE = 'unit_housing'
const COMMON_AREA_TYPE = 'common_area'
/**
 * Membership is `group_member`, not `part_of`. Both are aggregating and would
 * roll a space up into its lot identically, but both are also
 * `source_cardinality: "one"` — *per type*, since the conflict lookup in
 * `relation_service.go` is scoped by relation type. Spending `part_of` on a
 * grouping would therefore cost a space the edge that says what it is
 * physically part of; `group_member` is the slot the ontology reserves for
 * exactly this ("administrative grouping, no physical claim").
 */
const GROUP_MEMBER_RELATION = 'group_member'
const ASSET_RELATIONS_PATH = '/api/v1/organizations/{orgCode}/asset-relations'
/** The external-id source an asset's public code is stored under. */
const CODE_SOURCE = 'code'
/**
 * How far a window may be from a room and still be that room's (spec §17).
 * A floor is a metre-scale drawing; this is the slack for a room traced along
 * the inner face of its walls rather than along their centreline.
 */
const WINDOW_SPACE_TOLERANCE_MM = 400

/** One entry of the grouping combobox (spec 2026-08-30-01 §5). */
export interface GrouperOption {
    uid: string
    name: string
    code?: string
    typeCode: string
    /** The unit's physical nature, when it has one (spec 2026-08-30-03 §1). */
    unitKind?: string
}

/** An asset row as the grouper lists return it, with `include` applied. */
interface GrouperRow {
    uid?: string
    name?: string
    code?: string
    external_ids?: Record<string, unknown> | { source?: string; identifier?: string }[] | null
    properties?: { code?: string; value?: unknown }[] | null
}

/**
 * The asset's public code, read off the external ids under the `code` source.
 * Assets have no code column (spec 2026-08-30-03 §3), and the payload spells
 * the ids either way round depending on the endpoint, so both are accepted.
 */
function codeOfAsset(raw: GrouperRow): string | undefined {
    const ids = raw.external_ids
    if (!ids) return undefined
    if (Array.isArray(ids)) {
        const hit = ids.find((e) => e?.source === CODE_SOURCE)
        return hit?.identifier || undefined
    }
    const value = (ids as Record<string, unknown>)[CODE_SOURCE]
    return typeof value === 'string' && value ? value : undefined
}

/** The `unit_kind` value among the asset's embedded property values. */
function unitKindOfAsset(raw: GrouperRow): string | undefined {
    const hit = raw.properties?.find((p) => p?.code === UNIT_KIND_PROPERTY)
    return typeof hit?.value === 'string' && hit.value ? hit.value : undefined
}

/**
 * The `room_number` among an asset's embedded property values (spec
 * 2026-08-31-02 §5). Numbers survive a JSON round trip as numbers, but a value
 * written by an import or a CSV arrives as a string — both are the same number.
 */
function roomNumberOfAsset(raw: { properties?: { code?: string; value?: unknown }[] }): number | undefined {
    const hit = raw.properties?.find((p) => p?.code === ROOM_NUMBER_PROPERTY)
    const n = typeof hit?.value === 'string' ? Number(hit.value) : hit?.value
    return typeof n === 'number' && Number.isFinite(n) ? n : undefined
}

/**
 * space asset uid → its membership, read off the `group_member` edges.
 *
 * `group_member` is generic — an organization may group assets for reasons of
 * its own — so only `unit` / `common_area` targets count, and only those the
 * combobox is ever allowed to replace (§3).
 */
function groupersFromRelations(payload: unknown): Map<string, GrouperRef> {
    const map = new Map<string, GrouperRef>()
    type Row = {
        uid?: string
        source?: { uid?: string }
        target?: { uid?: string; name?: string; asset_type_code?: string }
    }
    for (const rel of ((payload as { items?: Row[] } | undefined)?.items ?? [])) {
        const sourceUid = rel.source?.uid
        const target = rel.target
        if (!sourceUid || !target?.uid || !target.asset_type_code) continue
        if (!isGrouperTypeCode(target.asset_type_code)) continue
        map.set(sourceUid, {
            assetUid: target.uid,
            name: target.name ?? target.uid,
            typeCode: target.asset_type_code,
            relationUid: rel.uid,
        })
    }
    return map
}
const REPRESENTATIONS_PATH = '/api/v1/organizations/{orgCode}/assets/{assetUid}/representations'
const ASSETS_PATH = '/api/v1/organizations/{orgCode}/assets'
const RISERS_PATH = '/api/v1/organizations/{orgCode}/assets/{assetUid}/plan/risers'

/** sessionStorage: the last run level picked per network (spec 2026-10-20-33). */
const RUN_LEVELS_KEY = 'plan-editor-run-levels'
/** The largest page `GET /assets` serves — asking for more is trimmed to this. */
const ASSETS_PAGE_SIZE_MAX = 100
const PROPERTIES_PATH = '/api/v1/organizations/{orgCode}/properties'
const PROPERTY_ASSET_TYPES_PATH = '/api/v1/organizations/{orgCode}/properties/{propertyUid}/asset-types'
const ASSET_PATH = '/api/v1/organizations/{orgCode}/assets/{assetUid}'
const REGISTRATIONS_PATH = '/api/v1/organizations/{orgCode}/assets/{assetUid}/frame/registrations'
/** Not keyed by frame: a copy is meant to travel between floors. */
const CLIPBOARD_KEY = 'plan-editor:clipboard'
/**
 * How long the view has to hold still before it is written to the URL. Long
 * enough that one pan or one wheel gesture produces a single commit, short
 * enough that the address bar is a usable link by the time you reach for it.
 */
const VIEW_URL_DEBOUNCE_MS = 1000

/**
 * The raster render URL, authenticated through the `token` query parameter —
 * an `<image href>` inside the stage cannot carry an Authorization header.
 * Same technique as `PlanCanvas`.
 */
function authedRenderUrl(org: string, repUid: string, dpi = 150): string {
    let token: string | null = null
    try {
        token = localStorage.getItem('token')
    } catch {
        // localStorage may be unavailable in sandboxed contexts.
    }
    const sep = token ? `&token=${encodeURIComponent(token)}` : ''
    return `${apiBaseUrl}/api/v1/organizations/${org}/representations/${repUid}/render?dpi=${dpi}&format=png${sep}`
}

/** An asset as the API returns it, before the type catalog resolves its code. */
interface RawAsset {
    uid: string
    name?: string | null
    asset_type_uid?: string | null
    parent_asset_uid?: string | null
}

interface PlanEditorPageProps {
    org: string
    assetUid: string
    /** `?calibrate=<registrationUid>` — the deep link from the frame
     *  registrations panel opens straight into calibrate mode (spec §6). */
    calibrateRegistrationUid?: string
    /**
     * `?focus=<assetUid>` — open on that asset, selected and centred. Written
     * back as the selection moves, so the URL in the address bar is always a
     * link to what is on screen.
     */
    focusAssetUid?: string
    /** `?center=<x>,<y>` — the millimetre point to put under the middle. */
    center?: string
    /** `?zoom=<px per mm>` — the scale to open at. */
    zoom?: number
    /**
     * The lot the editor was opened from, when `assetUid` is the floor frame
     * its plan is borrowed from. The breadcrumb names it and it starts as the
     * drawing target.
     */
    subject?: { uid: string; name: string }
}

export function PlanEditorPage({
    org, assetUid, calibrateRegistrationUid, focusAssetUid, center, zoom, subject,
}: PlanEditorPageProps) {
    const { t, i18n } = useTranslation('assets')
    const navigate = useNavigate()
    const isMobile = useIsMobile()
    // A state-backed callback ref, not a plain one: the scene can resolve while
    // the stage is unmounted (the asset-type gate renders a different branch
    // until `asset` and `assetTypes` are both in), and a ref mutation would not
    // re-run the engine-lifecycle effect — leaving the canvas permanently empty
    // on a direct URL load.
    const svgRef = useRef<SVGSVGElement | null>(null)
    const [stageEl, setStageEl] = useState<SVGSVGElement | null>(null)
    const attachStage = useCallback((node: SVGSVGElement | null) => {
        svgRef.current = node
        setStageEl(node)
    }, [])
    const engineRef = useRef<PlanEditor | null>(null)
    const baselineRef = useRef<SceneDoc | null>(null)

    const { request } = useApi()
    const queryClient = useQueryClient()
    const { data: asset } = useAsset(org, assetUid)
    const { data: assetTypes = [], isSuccess: assetTypesLoaded, isError: assetTypesFailed } = useAssetTypes(org, true)
    // Everything below the editing context, at any depth: the parent-resolution
    // rule (§6) walks from the room an element sits in up to the context, and
    // `GET /assets` without a filter caps at a page — far too small an index to
    // resolve a hierarchy against.
    // `include=properties` is here for `room_number` (spec 2026-08-31-02 §5):
    // the panel shows a saved room's number, and the next one it assigns is
    // max + 1 over exactly this list. `flags` is here for `auto_name` (§2): a
    // room whose stored name the server derived is edited as if it had none.
    // One query parameter rather than a second request per room.
    //
    // Every page, not one: `GET /assets` clamps `limit` to 100
    // (`base.ClampLimit(…, 20, 100)` in the assets handler) and says so only
    // in a `Warning` header, so a frame with 114 rooms silently lost the
    // flags and numbers of the last 14 — which the editor then treated as
    // typed names. `listQueryKey` keeps openapi-react-query's key shape so
    // `useSavePlan.invalidate` (which matches on the path) still refreshes it.
    const descendantsParams = useMemo(
        () => ({ path: { orgCode: org }, query: { ancestor_uid: assetUid, include: ['properties', 'flags'] } }),
        [org, assetUid],
    )
    const descendantsQuery = useQuery({
        queryKey: listQueryKey(ASSETS_PATH, descendantsParams),
        queryFn: async () => ({ data: await fetchAllPages<RawAsset>(ASSETS_PATH, descendantsParams, ASSETS_PAGE_SIZE_MAX) }),
    })
    // Direct children are also listed on their own: `?ancestor_uid` reads the
    // denormalized `parent_asset_ids` column, which can lag behind a freshly
    // created hierarchy, and the wall-owner selector (spec 2026-08-22-03 §5)
    // must offer the floor's units the moment they exist.
    //
    // The filter is `parent_asset_uid` — **not** `parent_uid`. The endpoint
    // ignores unknown query parameters silently, so a misspelling here does not
    // fail: it returns the organization's first page of assets, unfiltered,
    // which then pollutes `assetIndex` and sends the ancestor-resolution loop
    // below climbing chains that have nothing to do with this frame.
    const childrenQuery = $api.useQuery('get', ASSETS_PATH, {
        params: { path: { orgCode: org }, query: { parent_asset_uid: assetUid, limit: 200 } },
    })
    const sceneQuery = $api.useQuery('get', PLAN_SCENE_PATH, { params: { path: { orgCode: org, assetUid } } })
    const repsQuery = $api.useQuery('get', REPRESENTATIONS_PATH, { params: { path: { orgCode: org, assetUid } } })
    // The registrations' provenance carries the residual a plan proposal
    // fitted (spec 2026-10-21-11 §7); the scene does not.
    const registrationsQuery = $api.useQuery('get', REGISTRATIONS_PATH, { params: { path: { orgCode: org, assetUid } } })
    const residualByRegistration = useMemo(() => {
        const out = new Map<string, number>()
        for (const reg of registrationsQuery.data?.registrations ?? []) {
            const residual = (reg.provenance as { residual_mm?: number } | undefined)?.residual_mm
            if (typeof residual === 'number') out.set(reg.uid, residual)
        }
        return out
    }, [registrationsQuery.data])
    const planProposals = usePlanProposals(org, assetUid)
    const [proposalsOpen, setProposalsOpen] = useState(false)
    const patchRepresentation = $api.useMutation('patch', '/api/v1/organizations/{orgCode}/representations/{repUid}')
    // Locale-aware dates (docs/frontend/internationalization.md) — the
    // digitization provenance line prints one.
    const { formatDate } = useFormatters()
    const {
        save, saving, progress, writeRegistration, writeCalibration, invalidate, invalidateRegistrations,
    } = useSavePlan(org, assetUid)
    const registerUnderlay = $api.useMutation('post', REGISTRATIONS_PATH)
    const createImportedRep = $api.useMutation('post', REPRESENTATIONS_PATH)

    // ---- React mirrors of the engine's state (coarse events only) ----
    const [tool, setTool] = useState<ToolId>('select')
    const [selectedKey, setSelectedKey] = useState<string | null>(null)
    // How many elements the fields on screen are editing — shift-click builds
    // groups of like elements, and an edit then applies to all of them.
    const [selectionCount, setSelectionCount] = useState(0)
    /** Every selected key, primary first — the layer panel highlights them all. */
    const [selectedKeys, setSelectedKeys] = useState<string[]>([])
    const [dirty, setDirty] = useState(false)
    const [canUndo, setCanUndo] = useState(false)
    const [canRedo, setCanRedo] = useState(false)
    const [issues, setIssues] = useState<ValidationIssue[]>([])
    const [layers, setLayers] = useState(defaultLayerStates())
    const [snap, setSnap] = useState(true)
    // The grouping overlay is on by default: the whole point of the feature is
    // that a floor's split is visible without hunting for it (§8).
    const [showGroupers, setShowGroupers] = useState(true)
    // The engine mutates its document in place, so the shell keeps a wrapper
    // object whose identity changes on every engine event — that is what makes
    // the panels re-render without React owning the geometry.
    const [docState, setDocState] = useState<{ doc: SceneDoc; version: number } | null>(null)
    // The drawing target: the asset everything drawn next belongs to (spec
    // 2026-08-22-02 §7, generalized from walls to every tool). `null` is
    // **automatic** — the containing space for an equipment, the host wall's
    // owner for an opening, the context asset for a wall or a room — which is
    // the behaviour the specs describe and the default nobody has to think
    // about. Picking an asset overrides that walk for everything drawn next.
    // Opened from a lot drawn on this floor, the lot is picked: a room drawn
    // there belongs to it, as it did in the lot's own frame.
    const [drawParentUid, setDrawParentUid] = useState<string | null>(subject?.uid ?? null)
    // The type the equipment tool is armed with, chosen before the drop.
    const [armedEquipment, setArmedEquipment] = useState<PickedEquipmentType | null>(null)
    const [armedPickerOpen, setArmedPickerOpen] = useState(false)
    // The footprint the next one is placed with, remembered per type.
    const [armedSize, setArmedSize] = useState<EquipmentSize>({ width: 600, depth: 300 })
    const equipmentSizes = useEquipmentSizes(assetUid)
    const [pickerOpen, setPickerOpen] = useState(false)
    // Wall thickness is armed before the gesture and remembered per plan for the
    // browser session — a floor is traced at one or two thicknesses, and going
    // back to fix a wall drawn at the wrong one is the cost this avoids.
    const wallThickness = useWallThickness(assetUid)
    // The grouping picker's "Recent" shortcut group (spec 2026-09-06-06) — kept
    // per organization, so it follows a user filing a whole building frame by
    // frame, one floor after another.
    const { recentUids: recentGrouperUids, onPicked: onGrouperPicked } = useRecentGroupers(org)
    const [summary, setSummary] = useState<SaveOutcome | null>(null)
    // Assets the ontology has nowhere legal to file (spec 2026-09-18-01). The
    // save is refused while this is non-empty: the API would answer 422 and the
    // drawing would disappear with it.
    const [refusedParents, setRefusedParents] = useState<{ name: string; typeCode: string }[]>([])
    const [pendingLeave, setPendingLeave] = useState<{ proceed: () => void; reset: () => void } | null>(null)
    // Calibration (§1/§2), layer manipulation (§6) and import (§7).
    const [calibration, setCalibration] = useState<CalibrationState>({
        registrationUid: null, pairs: [], awaitingTarget: false, measuring: false, dirty: false,
        mode: 'placement', mmPerUnit: null, current: null, showCurrent: true,
    })
    const [calibrateApplying, setCalibrateApplying] = useState(false)
    const [calibrateError, setCalibrateError] = useState<string | null>(null)
    const [measureRequest, setMeasureRequest] = useState<{ a: Point; b: Point } | null>(null)
    const [measureDraft, setMeasureDraft] = useState('1000')
    const [selectedLayerUid, setSelectedLayerUid] = useState<string | null>(null)
    const [importing, setImporting] = useState(false)
    const [footprintNotice, setFootprintNotice] = useState(false)
    // A traced hole that fit in no room was dropped (spec 2026-10-19-07): said
    // until the next hole lands, the tool changes or the user dismisses it.
    const [holeRejected, setHoleRejected] = useState(false)
    const footprintNoticeShownRef = useRef(false)
    const [importNotice, setImportNotice] = useState<{ error: boolean; message: string } | null>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)
    // Digitization (spec 2026-09-03-01 §F1–§F5).
    const [digitizeOpen, setDigitizeOpen] = useState(false)
    // Each opening is a fresh dialog instance. Reopened from the Retry strip
    // while the previous close was still animating out, Radix re-portals the
    // overlay after the lingering content, and the overlay then sits on top of
    // the dialog and swallows every click in it.
    const [digitizeDialogKey, setDigitizeDialogKey] = useState(0)
    const openDigitizeDialog = useCallback(() => {
        setDigitizeDialogKey((k) => k + 1)
        setDigitizeOpen(true)
    }, [])
    // Spec 2026-09-30-04 §5: a run with validation errors waits here, unwritten.
    const [digitizePreview, setDigitizePreview] = useState<{ jobUid: string; output: DigitizeOutput } | null>(null)
    const [digitizePreviewError, setDigitizePreviewError] = useState<string | null>(null)
    const [autoCalibrateOpen, setAutoCalibrateOpen] = useState(false)
    // Spec 2026-09-30-02 §4: a low-confidence auto-calibration is a proposal
    // the user accepts or replaces by hand, never a silent write.
    const [calibrationProposal, setCalibrationProposal] = useState<{ jobUid: string; output: CalibrateOutput } | null>(null)
    const [proposalError, setProposalError] = useState<string | null>(null)
    const [digitizeReplace, setDigitizeReplace] = useState(false)
    const [promoteAllOpen, setPromoteAllOpen] = useState(false)

    const assetTypeByCode = useMemo(() => {
        const map = new Map<string, (typeof assetTypes)[number]>()
        for (const at of assetTypes) if (at.code) map.set(at.code, at)
        return map
    }, [assetTypes])

    const assetTypeByUid = useMemo(() => {
        const map = new Map<string, (typeof assetTypes)[number]>()
        for (const at of assetTypes) map.set(at.uid, at)
        return map
    }, [assetTypes])

    /**
     * `possible_parent_codes` of a type, resolved through the uid list the API
     * returns *and* through the inheritance chain — the ontology declares the
     * list on the trade, not on every equipment leaf.
     */
    const typeNodes = useMemo(() => {
        const byUid = new Map<string, AssetTypeNode>()
        const byCode = new Map<string, AssetTypeNode>()
        for (const at of assetTypes as unknown as {
            uid: string
            code?: string
            inherits_from_asset_type_uid?: string | null
            possible_parent_asset_type_uids?: string[]
        }[]) {
            const node: AssetTypeNode = {
                uid: at.uid,
                code: at.code,
                inheritsFromUid: at.inherits_from_asset_type_uid ?? undefined,
                possibleParentUids: at.possible_parent_asset_type_uids,
            }
            byUid.set(node.uid, node)
            if (node.code) byCode.set(node.code, node)
        }
        return { byUid, byCode }
    }, [assetTypes])

    const parentCodesOf = useCallback(
        (code: string): string[] => possibleParentCodes(code, typeNodes.byCode, typeNodes.byUid),
        [typeNodes],
    )

    /**
     * Can the grouper tool file anything under the frame being edited?
     *
     * A grouper is tree-parented to the frame (§4 of spec 2026-08-30-01) and is
     * a `unit` or a `common_area` — neither of which the ontology lets live
     * under a `unit`. In a lot's own editor the tool therefore produced a
     * guaranteed 422 at save ("This asset type cannot be placed under the
     * target parent's type"), and the only way out the inspector offered was
     * to re-parent the new lot onto the floor: a SIBLING unit named after its
     * type ("Lot"), with its outline registered into the first lot's frame
     * (spec 2026-09-23-02, the "Lot" artifact). A lot's interior is rooms, and
     * the room tool is right beside it, so the grouper is simply not offered
     * where nothing it makes can be stored.
     */
    const grouperAllowedHere = useMemo(() => {
        const frameType = asset?.asset_type_uid ? assetTypeByUid.get(asset.asset_type_uid)?.code : undefined
        if (!frameType) return true
        return [UNIT_TYPE, COMMON_AREA_TYPE].some((code) => {
            const parents = parentCodesOf(code)
            return parents.length === 0 || parents.includes(frameType)
        })
    }, [asset?.asset_type_uid, assetTypeByUid, parentCodesOf])
    const grouperAllowedRef = useRef(grouperAllowedHere)
    useEffect(() => {
        grouperAllowedRef.current = grouperAllowedHere
    }, [grouperAllowedHere])

    // Ancestors *above* the context are pulled in one hop at a time: the chain is
    // at most site → building → floor → unit, and resolving it lazily beats
    // listing an organization's whole asset tree up front.
    // The raw record, not a resolved `AssetLite`: an ancestor can land before
    // the asset-type catalog does, and an entry written once with an
    // unresolved `typeCode` would stay type-less forever — which silently
    // breaks every rule that keys on a type (frame anchors, parent codes).
    const [ancestors, setAncestors] = useState<Record<string, RawAsset>>({})
    // Assets pulled in by "link to an existing asset": they live outside the
    // frame's subtree by definition, so nothing else would index them.
    const [linkedAssets, setLinkedAssets] = useState<Record<string, RawAsset>>({})

    const assetIndex = useMemo(() => {
        const map = new Map<string, AssetLite>()
        const add = (a: { uid?: string; name?: string | null; asset_type_uid?: string | null; parent_asset_uid?: string | null; flags?: string[] | null }) => {
            if (!a?.uid) return
            map.set(a.uid, {
                uid: a.uid,
                name: a.name ?? undefined,
                typeCode: a.asset_type_uid ? assetTypeByUid.get(a.asset_type_uid)?.code : undefined,
                parentUid: a.parent_asset_uid ?? undefined,
                // Only the descendants list asks for flags; a record from another
                // source says nothing either way rather than "not auto-named".
                ...(a.flags ? { autoName: a.flags.includes(AUTO_NAME_FLAG) } : {}),
            })
        }
        if (asset) add(asset as never)
        for (const a of (descendantsQuery.data?.data ?? []) as never[]) add(a)
        for (const a of (childrenQuery.data?.data ?? []) as never[]) add(a)
        for (const a of Object.values(ancestors)) add(a as never)
        for (const a of Object.values(linkedAssets)) if (!map.has(a.uid)) add(a as never)
        return map
    }, [asset, descendantsQuery.data, childrenQuery.data, ancestors, linkedAssets, assetTypeByUid])

    /** Existing rooms' stored `room_number`, by asset uid. */
    const roomNumberByUid = useMemo(() => {
        const map = new Map<string, number>()
        for (const raw of (descendantsQuery.data?.data ?? []) as { uid?: string; properties?: { code?: string; value?: unknown }[] }[]) {
            const n = raw?.uid ? roomNumberOfAsset(raw) : undefined
            if (raw?.uid && n !== undefined) map.set(raw.uid, n)
        }
        return map
    }, [descendantsQuery.data])

    /** The assets whose stored name is the server's derivation (`auto_name`). */
    const autoNamedUids = useMemo(() => {
        const set = new Set<string>()
        for (const raw of (descendantsQuery.data?.data ?? []) as { uid?: string; flags?: string[] | null }[]) {
            if (raw?.uid && raw.flags?.includes(AUTO_NAME_FLAG)) set.add(raw.uid)
        }
        return set
    }, [descendantsQuery.data])
    /**
     * Every asset the descendants list actually described. Only for those can
     * "not in `autoNamedUids`" mean "not auto-named": an archived room is still
     * drawn but never listed, and the scene payload is what describes it.
     */
    const listedUids = useMemo(() => {
        const set = new Set<string>()
        for (const raw of (descendantsQuery.data?.data ?? []) as { uid?: string }[]) if (raw?.uid) set.add(raw.uid)
        return set
    }, [descendantsQuery.data])

    /**
     * Which asset types carry a room number, straight from the ontology (spec
     * 2026-08-31-02 §6) — two cached lookups rather than a list in the
     * frontend, so binding the property to a fourth type is a provisioning
     * change and nothing else. An organization that has no such property gets
     * an empty set and no Number field.
     */
    const roomNumberDefQuery = $api.useQuery('get', PROPERTIES_PATH, {
        params: { path: { orgCode: org }, query: { code: ROOM_NUMBER_PROPERTY, limit: 1 } },
    })
    const roomNumberDefUid = ((roomNumberDefQuery.data?.data ?? []) as { uid?: string }[])[0]?.uid
    const roomNumberTypesQuery = $api.useQuery(
        'get',
        PROPERTY_ASSET_TYPES_PATH,
        { params: { path: { orgCode: org, propertyUid: roomNumberDefUid ?? '' } } },
        { enabled: Boolean(roomNumberDefUid) },
    )
    const roomNumberTypes = useMemo(() => {
        const raw = roomNumberTypesQuery.data as { data?: { code?: string }[] } | { code?: string }[] | undefined
        const rows = Array.isArray(raw) ? raw : raw?.data ?? []
        return new Set(rows.map((t) => t.code).filter((c): c is string => Boolean(c)))
    }, [roomNumberTypesQuery.data])

    /**
     * The number a room of this type under this parent should take: one past
     * the highest already in use, counting both saved rooms and the ones this
     * session has drawn. Numbers are stable, not dense — deleting Chambre 2
     * leaves the next one at 4, which is the honest answer when Chambre 3 is
     * still on the plan.
     */
    const nextRoomNumber = useCallback((typeCode: string, parentUid?: string): number => {
        let max = 0
        for (const a of assetIndex.values()) {
            if (a.typeCode !== typeCode) continue
            if (parentUid && a.parentUid !== parentUid) continue
            const n = roomNumberByUid.get(a.uid)
            if (n !== undefined && n > max) max = n
        }
        for (const el of engineRef.current?.getDoc().elements ?? []) {
            if (el.kind !== 'space' || ownerTypeCode(el.owner) !== typeCode) continue
            const owner = el.owner
            const elParent = owner.newAsset?.parentUid ?? owner.assetParentUid
            if (parentUid && elParent !== parentUid) continue
            const n = ownerRoomNumber(owner)
            if (n !== undefined && n > max) max = n
        }
        return max + 1
    }, [assetIndex, roomNumberByUid])

    const missingAncestorUid = useMemo(() => {
        for (const a of assetIndex.values()) {
            if (a.parentUid && !assetIndex.has(a.parentUid)) return a.parentUid
        }
        return undefined
    }, [assetIndex])

    const ancestorQuery = $api.useQuery(
        'get',
        ASSET_PATH,
        { params: { path: { orgCode: org, assetUid: missingAncestorUid ?? '' } } },
        { enabled: Boolean(missingAncestorUid) },
    )

    useEffect(() => {
        const found = ancestorQuery.data as RawAsset | undefined
        if (!found?.uid) return
        setAncestors((prev) => (prev[found.uid] ? prev : { ...prev, [found.uid]: found }))
    }, [ancestorQuery.data])

    /* ---- grouping: units and common areas (spec 2026-08-30-01) ---- */

    /**
     * The scope the grouping combobox lists from: the building group above this
     * frame (§5). Until the ancestor climb has reached it — it arrives one hop
     * at a time — the highest ancestor already known stands in, which is a
     * subset of the same site and never a different one.
     */
    const grouperScopeUid = useMemo(() => {
        let cursor: string | undefined = assetUid
        let highest = assetUid
        const seen = new Set<string>()
        for (let i = 0; i < 12 && cursor; i++) {
            if (seen.has(cursor)) break
            seen.add(cursor)
            const a = assetIndex.get(cursor)
            if (a?.typeCode === 'building_group') return a.uid
            highest = cursor
            cursor = a?.parentUid
        }
        return highest
    }, [assetIndex, assetUid])

    /**
     * The scope of the inspector's "link to an existing asset" and re-parent
     * searches: the building this frame belongs to, not the floor. The assets
     * worth linking are the ones shared across floors (an elevator, a shaft),
     * and they hang under the building. Until the climb reaches a building, the
     * highest ancestor known stands in, as for the grouping scope.
     */
    const linkScopeUid = useMemo(() => {
        let cursor: string | undefined = assetUid
        let highest = assetUid
        const seen = new Set<string>()
        for (let i = 0; i < 12 && cursor; i++) {
            if (seen.has(cursor)) break
            seen.add(cursor)
            const a = assetIndex.get(cursor)
            const base = a?.typeCode ? lotBaseTypeCode(a.typeCode) : undefined
            if (base === 'building' || base === 'building_group') return cursor
            highest = cursor
            cursor = a?.parentUid
        }
        return highest
    }, [assetIndex, assetUid])

    /** Ancestors of the plan's asset, root first: the part of a path every link-search result shares with it. */
    const sharedAncestorUids = useMemo(() => {
        const chain: string[] = []
        const seen = new Set<string>([assetUid])
        let cursor = assetIndex.get(assetUid)?.parentUid
        while (cursor && !seen.has(cursor) && chain.length < 12) {
            seen.add(cursor)
            chain.unshift(cursor)
            cursor = assetIndex.get(cursor)?.parentUid
        }
        return chain
    }, [assetIndex, assetUid])

    // Two calls rather than one: `asset_type_code` takes a single code, and the
    // combobox shows the two families in their own sections anyway.
    //
    // `include` brings back each grouper's code and `unit_kind` in the same
    // call, which is what lets the inspector show a saved lot's kind without a
    // second round trip per selection (spec 2026-08-30-03 §4).
    const GROUPER_INCLUDE = ['properties', 'external_ids'] as const
    const unitsQuery = $api.useQuery('get', ASSETS_PATH, {
        params: {
            path: { orgCode: org },
            query: {
                ancestor_uid: grouperScopeUid, asset_type_code: UNIT_TYPE, limit: 500,
                include: [...GROUPER_INCLUDE],
            },
        },
    })
    const commonAreasQuery = $api.useQuery('get', ASSETS_PATH, {
        params: {
            path: { orgCode: org },
            query: {
                ancestor_uid: grouperScopeUid, asset_type_code: COMMON_AREA_TYPE, limit: 500,
                include: [...GROUPER_INCLUDE],
            },
        },
    })

    /**
     * The `group_member` edges of everything under this frame — the memberships the
     * combobox reflects, and the baseline the save diffs against (§9). Loaded
     * in ONE call thanks to `source_ancestor_uid`, and the editor waits for it
     * before building: an engine seeded without the edges would read every
     * existing membership as a fresh assignment on the next save.
     */
    const relationsQuery = $api.useQuery('get', ASSET_RELATIONS_PATH, {
        params: {
            path: { orgCode: org },
            query: { relation_type: GROUP_MEMBER_RELATION, source_ancestor_uid: assetUid, limit: 500 },
        },
    })

    const grouperOptions: GrouperOption[] = useMemo(() => {
        const out: GrouperOption[] = []
        const push = (rows: unknown[], typeCode: string) => {
            for (const raw of rows as GrouperRow[]) {
                if (!raw?.uid) continue
                out.push({
                    uid: raw.uid,
                    name: raw.name ?? raw.uid,
                    code: raw.code ?? codeOfAsset(raw),
                    typeCode,
                    ...(unitKindOfAsset(raw) ? { unitKind: unitKindOfAsset(raw) } : {}),
                })
            }
        }
        push((unitsQuery.data?.data ?? []) as unknown[], UNIT_TYPE)
        push((commonAreasQuery.data?.data ?? []) as unknown[], COMMON_AREA_TYPE)
        return out
    }, [unitsQuery.data, commonAreasQuery.data])

    const grouperBySpaceUid = useMemo(
        () => groupersFromRelations(relationsQuery.data),
        [relationsQuery.data],
    )
    // Same shape as `ctxRef` below: an effect, not a render-time assignment.
    // The engine is only ever built from a render *after* the memberships have
    // settled (see the seed guard), so the ref is populated by then.
    const grouperBySpaceRef = useRef(grouperBySpaceUid)
    useEffect(() => {
        grouperBySpaceRef.current = grouperBySpaceUid
    }, [grouperBySpaceUid])

    /** Grouper asset uid → its stored code and kind, for `applyGroupers`. */
    const grouperMeta = useMemo(() => {
        const map = new Map<string, { code?: string; unitKind?: string }>()
        for (const o of grouperOptions) {
            map.set(o.uid, {
                ...(o.code ? { code: o.code } : {}),
                ...(o.unitKind ? { unitKind: o.unitKind } : {}),
            })
        }
        return map
    }, [grouperOptions])
    const grouperMetaRef = useRef(grouperMeta)
    useEffect(() => {
        grouperMetaRef.current = grouperMeta
    }, [grouperMeta])
    // Same reason as the two refs above: `applyGroupers` runs from callbacks the
    // engine holds, which must not be rebuilt when a query settles.
    const roomNumberRef = useRef(roomNumberByUid)
    useEffect(() => {
        roomNumberRef.current = roomNumberByUid
    }, [roomNumberByUid])
    const autoNamedRef = useRef(autoNamedUids)
    useEffect(() => {
        autoNamedRef.current = autoNamedUids
    }, [autoNamedUids])
    const listedRef = useRef(listedUids)
    useEffect(() => {
        listedRef.current = listedUids
    }, [listedUids])

    /**
     * Stamps the loaded memberships onto the spaces of a freshly built doc.
     * `rows` is passed straight after a refetch, when the query cache has not
     * re-rendered yet and the ref still holds the pre-save picture.
     */
    const applyGroupers = useCallback((doc: SceneDoc, rows?: unknown): SceneDoc => {
        const byUid = rows === undefined ? grouperBySpaceRef.current : groupersFromRelations(rows)
        for (const el of doc.elements) {
            if (el.kind !== 'space' || !el.owner.assetUid) continue
            const ref = byUid.get(el.owner.assetUid)
            if (ref) el.owner.grouper = { ...ref }
            else delete el.owner.grouper
            // The stored room number rides along: the panel shows it, the
            // drawing labels an unnamed room with it, and the diff measures the
            // panel's edit against it (spec 2026-08-31-02 §5). Unlike a
            // grouper's code it *is* stamped on the document — a number is what
            // an unnamed room is called, so the renderer needs it in hand.
            //
            // The scene payload stamps both the number and the `auto_name`
            // flag itself (§2), so the list only *overrides* what it actually
            // described: for an asset it never listed — archived, or drawn
            // here from another subtree — the scene's word stands. Read the
            // reverse way, an absent list entry used to strip a still-drawn
            // archived room of its flag, and it was edited as a typed name.
            const listed = listedRef.current.has(el.owner.assetUid)
            const number = roomNumberRef.current.get(el.owner.assetUid)
            if (number !== undefined) el.owner.assetRoomNumber = number
            else if (listed) delete el.owner.assetRoomNumber
            if (autoNamedRef.current.has(el.owner.assetUid)) el.owner.assetAutoName = true
            else if (listed) delete el.owner.assetAutoName
        }
        return doc
    }, [])

    const localizedTypeLabel = useCallback((code: string): string => {
        const type = assetTypeByCode.get(code) as { name?: unknown } | undefined
        const name = type?.name
        if (typeof name === 'string') return name
        if (name && typeof name === 'object') {
            const m = name as Record<string, string>
            return m[i18n.language] || m.en || m.fr || code
        }
        return code
    }, [assetTypeByCode, i18n.language])

    /**
     * The network catalogue (the ontology's `network_kind` enum), as the
     * scene served it, and its labels in the UI language. Runs, the layer
     * panel, the run tool and the inspector all read this one list.
     */
    const networks = useMemo(() => sanitizeNetworks(sceneQuery.data?.networks), [sceneQuery.data])
    const networkLabelOf = useCallback((code: string): string => {
        const name = networks.find((n) => n.code === code)?.name as Record<string, string> | undefined
        return (name && (name[i18n.language] || name.en || name.fr || Object.values(name)[0])) || code
    }, [networks, i18n.language])
    const networkLabelRef = useRef(networkLabelOf)
    useEffect(() => {
        networkLabelRef.current = networkLabelOf
    }, [networkLabelOf])
    /**
     * The asset types a run can be linked to: the ontology's `*_network`
     * family (duct, chilled water, heating, water distribution…), read off the
     * catalog so a type an organization adds is offered without a release.
     */
    const networkAssetTypeCodes = useMemo(
        () => assetTypes.map((at) => at.code ?? '').filter((code) => code.endsWith('_network')),
        [assetTypes],
    )

    /**
     * Where a run's network asset is filed (spec 2026-10-20-33): the frame
     * when it is a floor (or its nearest floor), its building, its site.
     */
    const levelParents = useMemo(
        () => levelParentsOf(assetUid, (uid) => assetIndex.get(uid), (code) => lotBaseTypeCode(code) === 'building'),
        [assetUid, assetIndex],
    )
    /** The site, or the building when the tree stops there: what network searches cover. */
    const networkScopeUid = levelParents.site?.uid ?? levelParents.building?.uid ?? levelParents.floor?.uid

    // The network assets already filed at a level of this frame: what an
    // unlinked run reuses before anything is created. `network_kind` comes
    // with them, it is what tells a supply duct from a return one.
    const networkAssetsQuery = $api.useQuery('get', ASSETS_PATH, {
        params: {
            path: { orgCode: org },
            query: {
                ancestor_uid: networkScopeUid,
                asset_type_code: networkAssetTypeCodes.join(','),
                include: ['properties'],
                limit: 500,
            },
        },
    }, { enabled: Boolean(networkScopeUid) && networkAssetTypeCodes.length > 0 })
    const existingNetworkAssets = useMemo((): NetworkAssetRow[] => {
        const levelUids = new Set(Object.values(levelParents).map((p) => p?.uid))
        const rows = (networkAssetsQuery.data?.data ?? []) as unknown as (RawAsset & { properties?: { code?: string; value?: unknown }[] })[]
        return rows
            .filter((r) => r.uid && r.parent_asset_uid && levelUids.has(r.parent_asset_uid))
            .map((r) => {
                const kind = r.properties?.find((p) => p?.code === NETWORK_KIND_PROPERTY)?.value
                return {
                    uid: r.uid,
                    name: r.name ?? r.uid,
                    typeCode: r.asset_type_uid ? assetTypeByUid.get(r.asset_type_uid)?.code ?? undefined : undefined,
                    parentUid: r.parent_asset_uid ?? undefined,
                    ...(typeof kind === 'string' && kind ? { networkKind: kind } : {}),
                }
            })
    }, [networkAssetsQuery.data, levelParents, assetTypeByUid])

    // Auto-created network assets are named in the organization's language.
    const orgLocaleQuery = $api.useQuery('get', '/api/v1/organizations/{orgCode}/settings', {
        params: { path: { orgCode: org }, query: { codes: 'locale', kind: 'setting' } },
    })
    const orgLocale = useMemo(() => {
        const value = (orgLocaleQuery.data?.data ?? []).find((e) => e.code === 'locale')?.value
        return typeof value === 'string' && value ? value : i18n.language
    }, [orgLocaleQuery.data, i18n.language])
    const networkCtx = useMemo((): NetworkContext => ({
        networks,
        parents: levelParents,
        existing: existingNetworkAssets,
        labelOf: (code) => {
            const name = networks.find((n) => n.code === code)?.name as Record<string, string> | undefined
            return (name && (name[orgLocale] || name.en || name.fr || Object.values(name)[0])) || code
        },
    }), [networks, levelParents, existingNetworkAssets, orgLocale])
    const runLevelOfRun = useCallback(
        (run: RunElement) => runLevel(run, networkCtx, (uid) => assetIndex.get(uid)?.parentUid),
        [networkCtx, assetIndex],
    )
    const runTargetOfRun = useCallback((run: RunElement) => resolveRunTarget(run, networkCtx), [networkCtx])
    // A new floor segment joins its building's network on save (spec
    // 2026-10-20-35); Move to building level names the one it creates.
    const runSegmentAssemblyOf = useCallback((run: RunElement) => {
        const target = resolveRunTarget(run, networkCtx)
        return target?.kind === 'pending' ? segmentAssembly(target.pending, networkCtx) : undefined
    }, [networkCtx])
    const runAssemblyNameOf = useCallback((run: RunElement) => {
        const building = levelParents.building
        return building ? networkAssetName(networkCtx.labelOf(run.network), building.name) : undefined
    }, [networkCtx, levelParents])
    const networkCtxRef = useRef(networkCtx)
    useEffect(() => {
        networkCtxRef.current = networkCtx
    }, [networkCtx])

    // What the run tool draws next — read by the engine at the moment a run
    // is finished, so a ref like the armed equipment type.
    const [armedNetwork, setArmedNetwork] = useState<string | null>(null)
    const [armedRunWidth, setArmedRunWidth] = useState<number | null>(null)
    // The last level picked per network, for the session: heating risers
    // drawn at Building level on ten floors are switched once.
    const [runLevels, setRunLevels] = useState<Record<string, NetworkLevel>>(() => {
        try {
            return JSON.parse(sessionStorage.getItem(RUN_LEVELS_KEY) ?? '{}') as Record<string, NetworkLevel>
        } catch {
            return {}
        }
    })
    const rememberRunLevel = useCallback((network: string, level: NetworkLevel) => {
        setRunLevels((prev) => {
            const next = { ...prev, [network]: level }
            try {
                sessionStorage.setItem(RUN_LEVELS_KEY, JSON.stringify(next))
            } catch {
                // A convenience: the switch still works for this page.
            }
            return next
        })
    }, [])
    const armedNetworkCode = armedNetwork ?? networks[0]?.code
    const armedRunLevel = effectiveLevel(
        (armedNetworkCode && runLevels[armedNetworkCode])
            || catalogueLevel(networks.find((n) => n.code === armedNetworkCode)),
        levelParents,
    )
    const runArmRef = useRef<{ network?: string; widthMm?: number; level?: NetworkLevel }>({})
    useEffect(() => {
        runArmRef.current = {
            network: armedNetworkCode,
            ...(armedRunWidth ? { widthMm: armedRunWidth } : {}),
            ...(armedRunLevel ? { level: armedRunLevel } : {}),
        }
    }, [armedNetworkCode, armedRunWidth, armedRunLevel])

    // What the riser tool places next (spec 2026-10-19-03 §1), read at the click.
    const [armedRiserNetwork, setArmedRiserNetwork] = useState<string | null>(null)
    const [armedRiserDiameter, setArmedRiserDiameter] = useState<number | null>(null)
    const [armedRiserPassage, setArmedRiserPassage] = useState<RiserPassage | undefined>(undefined)
    const armedRiserNetworkCode = armedRiserNetwork ?? networks[0]?.code
    const riserArmRef = useRef<{ network?: string; diameterMm?: number; passage?: RiserPassage }>({})
    useEffect(() => {
        riserArmRef.current = {
            network: armedRiserNetworkCode,
            ...(armedRiserDiameter ? { diameterMm: armedRiserDiameter } : {}),
            ...(armedRiserPassage ? { passage: armedRiserPassage } : {}),
        }
    }, [armedRiserNetworkCode, armedRiserDiameter, armedRiserPassage])

    // The building's columns, so a riser linked to one lands where the column
    // already crosses the other floors: columns are vertical.
    const riserBuildingUid = levelParents.building?.uid
    const risersQuery = $api.useQuery('get', RISERS_PATH, {
        params: { path: { orgCode: org, assetUid: riserBuildingUid ?? '' } },
    }, { enabled: !!riserBuildingUid })
    const riserAlignmentFor = useCallback((columnUid: string): { point: Point; levelName: string } | undefined => {
        const diagram = risersQuery.data
        if (!diagram) return undefined
        const here = diagram.levels.findIndex((l) => l.asset_uid === assetUid)
        if (here < 0) return undefined
        const column = diagram.columns.find((c) => c.asset_uid === columnUid)
        let best: { index: number; at: Point } | undefined
        for (const entry of column?.levels ?? []) {
            const index = diagram.levels.findIndex((l) => l.asset_uid === entry.level_uid)
            if (!entry.riser || index < 0 || index === here) continue
            if (!best || Math.abs(index - here) < Math.abs(best.index - here)) best = { index, at: entry.riser.at }
        }
        if (!best) return undefined
        const toTransform = (t: { tx?: number; ty?: number; rotation_deg?: number; scale?: number }) =>
            ({ tx: t.tx ?? 0, ty: t.ty ?? 0, rotation_deg: t.rotation_deg ?? 0, scale: t.scale ?? 1 })
        const from = diagram.levels[best.index].building_transform
        const to = diagram.levels[here].building_transform
        // Through the building's frame when both floors are registered there;
        // otherwise the floors are taken to share one frame.
        const point = from && to
            ? applyTransform(invertTransform(toTransform(to)), applyTransform(toTransform(from), best.at))
            : best.at
        return { point, levelName: diagram.levels[best.index].name }
    }, [risersQuery.data, assetUid])

    // Which networks the layer panel hides — a display switch, never an edit.
    const [hiddenNetworks, setHiddenNetworks] = useState<ReadonlySet<string>>(() => new Set())
    const hiddenNetworksRef = useRef(hiddenNetworks)
    useEffect(() => {
        hiddenNetworksRef.current = hiddenNetworks
        engineRef.current?.setHiddenNetworks(hiddenNetworks)
    }, [hiddenNetworks])
    const onToggleNetworks = useCallback((codes: string[], hidden: boolean) => {
        setHiddenNetworks((prev) => {
            const next = new Set(prev)
            for (const code of codes) {
                if (hidden) next.add(code)
                else next.delete(code)
            }
            return next
        })
    }, [])

    const openingTypes = useMemo(
        () => assetTypes
            .filter((at) => at.code?.startsWith('openings_'))
            .map((at) => ({ code: at.code as string, label: localizedTypeLabel(at.code as string) }))
            .sort((a, b) => a.label.localeCompare(b.label)),
        [assetTypes, localizedTypeLabel],
    )

    /**
     * `room` and its sub-types (spec 2026-08-31-01 §7). Read off the `room_`
     * prefix, which is the ontology's own contract for the family, and kept in
     * the order the catalog returns with the plain `room` first — "a room" is
     * the default a space is drawn as, and the alternatives read as
     * refinements of it.
     */
    const roomTypes = useMemo(() => {
        const label = (code: string) => ({ code, label: localizedTypeLabel(code) })
        const subTypes = assetTypes
            .filter((at) => at.code?.startsWith(`${ROOM_TYPE}_`))
            .map((at) => label(at.code as string))
            .sort((a, b) => a.label.localeCompare(b.label))
        return assetTypeByCode.has(ROOM_TYPE) ? [label(ROOM_TYPE), ...subTypes] : subTypes
    }, [assetTypes, assetTypeByCode, localizedTypeLabel])

    /** The same list as a set of codes — "is this drawing a room?". */
    const roomTypeCodes = useMemo(() => new Set(roomTypes.map((r) => r.code)), [roomTypes])

    /**
     * The first of `preferred` the organization's ontology actually declares.
     * `openings_interior_door` ships with spec 2026-08-22-01's provisioning, but
     * an org whose types predate it must still get a usable door rather than a
     * save that fails on an unknown type code.
     */
    const resolveTypeCode = useCallback((preferred: string[]): string | undefined =>
        preferred.find((code) => assetTypeByCode.has(code)), [assetTypeByCode])

    // Refs so the engine's hooks always see current data without being rebuilt.
    // Same reason as `armedRef`: the engine must not be rebuilt when this
    // callback's identity changes.
    const onEquipmentMovedRef = useRef<(key: string) => void>(() => {})

    // The engine reads the armed type at click time, so it must not be rebuilt
    // when the pick changes — a ref, like the drawing context above.
    const armedRef = useRef<PickedEquipmentType | null>(armedEquipment)
    const armedSizeRef = useRef<EquipmentSize>(armedSize)
    useEffect(() => {
        armedRef.current = armedEquipment
        armedSizeRef.current = armedSize
    }, [armedEquipment, armedSize])

    const ctxRef = useRef({ parentCodesOf, assetIndex, localizedTypeLabel, drawParentUid, resolveTypeCode })
    useEffect(() => {
        ctxRef.current = { parentCodesOf, assetIndex, localizedTypeLabel, drawParentUid, resolveTypeCode }
    }, [parentCodesOf, assetIndex, localizedTypeLabel, drawParentUid, resolveTypeCode])

    // The catalog lands *after* the engine is built, and a room nobody named is
    // labelled from it — without this the plan keeps printing the raw
    // `room_bedroom` code its first render fell back to, while the panel and
    // the layer list (plain React) already read "Bedroom 1". A language switch
    // moves every derived label for the same reason. **After** the effect
    // above, deliberately: effects run in declaration order, and rendering
    // before `ctxRef` is refreshed would re-read the very labels it is here to
    // replace.
    useEffect(() => {
        engineRef.current?.render()
    }, [localizedTypeLabel])

    const syncFromEngine = useCallback(() => {
        const engine = engineRef.current
        if (!engine) return
        setTool(engine.tool)
        setCanUndo(engine.canUndo)
        setCanRedo(engine.canRedo)
        setIssues(engine.issues)
        setLayers({ ...engine.layers })
        setDocState((prev) => ({ doc: engine.getDoc(), version: (prev?.version ?? 0) + 1 }))
    }, [])

    /* ---- element ownership: drawing is inventorying (§5, §6) ---- */

    /**
     * Where will this asset's geometry resolve *from*? Its nearest
     * ancestor-or-self frame anchor (spec 2026-08-25-03 §1). Parenting anything
     * to a room of a **unit** — or to the unit itself — anchors it on the unit,
     * not on the floor being edited, so its footprint on this layer is a
     * cross-layer one and needs the explicit row that says which layer it
     * belongs to. Without it the drawing would resolve into the unit's frame and
     * silently vanish from the floor it was traced on.
     */
    const isCrossLayer = useCallback((resolution: ParentResolution): boolean => {
        const ctx = ctxRef.current
        const pendingParent = resolution.parentKey
            ? engineRef.current?.getDoc().elements
                .find((el) => el.owner.newAsset?.key === resolution.parentKey)?.owner.newAsset
            : undefined
        const chainStart = pendingParent
            ? (FRAME_ANCHOR_TYPE_CODES.includes(lotBaseTypeCode(pendingParent.typeCode)) ? undefined : pendingParent.parentUid)
            : resolution.parentUid
        const frameOfChain = pendingParent && !chainStart
            ? assetUid
            : implicitFrameUid(chainStart, ctx.assetIndex)
        return frameOfChain !== undefined && frameOfChain !== assetUid
    }, [assetUid])

    const ownerForCreated = useCallback((
        kind: 'space' | 'grouper' | 'opening' | 'equipment',
        point: Point,
        hint?: { openingKind?: 'door' | 'window'; typeCode?: string; wallOwnerUid?: string; wallThickness?: number },
    ): ElementOwner => {
        const engine = engineRef.current
        const ctx = ctxRef.current
        // A grouper drawn with the split tool is a `unit` by default and the
        // panel's type select turns it into a `common_area`, exactly the way a
        // space defaults to `room` (spec 2026-08-30-01 §7). It is tree-parented
        // to the floor being edited, full stop — the walk has nothing to
        // arbitrate.
        if (kind === 'grouper') {
            return {
                // Derived from the type, never hardcoded — and re-derived when
                // the panel's type select changes it. A `unit` anchors a frame
                // of its own, so its outline traced on a floor is a cross-layer
                // footprint and the registration row is what says it is drawn
                // *here* (spec 2026-08-25-04 §1); without it the outline would
                // resolve into the unit's own frame and vanish from the floor.
                // A `common_area` anchors nothing, resolves to the floor
                // implicitly, and a row for it would be exactly the identity
                // registration `useSavePlan` refuses to write.
                ...(typeAnchorsFrame(NEW_GROUPER_TYPE) ? { crossLayer: true } : {}),
                newAsset: {
                    key: nextKey('asset'),
                    name: '',
                    typeCode: NEW_GROUPER_TYPE,
                    parentUid: assetUid,
                    parentFallback: false,
                },
            }
        }
        const typeCode = kind === 'space'
            // A digitized space proposes its own room sub-type
            // (`properties.type_code`, spec 2026-09-03-01 §B1). The catalog
            // still arbitrates: an org that lacks the sub-type falls back to
            // plain `room`, exactly as the panel's Type select does.
            ? ctx.resolveTypeCode(hint?.typeCode ? [hint.typeCode, ROOM_TYPE] : [ROOM_TYPE]) ?? ROOM_TYPE
            : hint?.typeCode
                ?? (kind === 'opening'
                    ? ctx.resolveTypeCode(hint?.openingKind === 'window'
                        ? [DEFAULT_WINDOW_TYPE, 'openings_custom']
                        : [DEFAULT_DOOR_TYPE, 'openings_entry_door', 'openings_custom'])
                    : undefined)
        if (!typeCode) return {}
        // The toolbar's drawing target (§7, generalized to every tool): picking
        // an asset there says "whatever I draw next belongs to that one", so it
        // starts the parent walk for every kind. The ontology still arbitrates —
        // a target whose type cannot hold this one is walked past like any other
        // candidate. Left on the context asset it changes nothing.
        const picked = ctx.drawParentUid ?? undefined
        // §2's carve-out: an opening belongs to the wall it is cut into, so the
        // walk starts at that wall's asset and the containing space is never
        // consulted — a door's centre sits *on* a room boundary by
        // construction, which `containingSpace` reads as inside. A room is no
        // more "inside" whatever polygon happens to contain its centre, so it
        // anchors on the target too; only equipment reads the point.
        const startFromUid = picked
            // §2's carve-out anchors an opening on its wall — except a
            // **window**, which belongs to the room it lights (§17). Leaving it
            // unanchored is what lets the space lookup below answer.
            ?? (kind === 'opening'
                ? (hint?.openingKind === 'window' ? undefined : hint?.wallOwnerUid)
                : kind === 'space' ? assetUid : undefined)
        const resolution = engine
            ? resolveCreatedAssetParent({
                point,
                doc: engine.getDoc(),
                assets: ctx.assetIndex,
                possibleParentCodes: ctx.parentCodesOf(typeCode),
                contextAssetUid: assetUid,
                ...(startFromUid ? { startFromUid } : {}),
                // Nothing picked, and an equipment reads the point: the space
                // it was dropped in is the parent, ontology or no ontology.
                ...(kind === 'equipment' && !picked ? { preferContainingSpace: true } : {}),
                // A window sits *on* the room's boundary, so containment is
                // decided by how the room happened to be traced. Distance is
                // the honest measure: the nearest space within about a wall's
                // thickness is the room it lights, and beyond that the walk
                // (confined to the frame) lands on the floor (§17).
                ...(kind === 'opening' && hint?.openingKind === 'window' && !picked
                    ? {
                        preferContainingSpace: true,
                        nearestSpaceToleranceMm: Math.max((hint.wallThickness ?? 0) * 1.5, WINDOW_SPACE_TOLERANCE_MM),
                    }
                    : {}),
                // Nothing picked: whatever this is, it belongs to the layer it
                // was drawn on. Only the pick above can send a footprint to
                // another layer.
                ...(picked ? {} : { confineToFrame: true }),
            })
            : { parentUid: startFromUid ?? assetUid, fallback: false, parentKey: undefined }
        const crossLayer = isCrossLayer(resolution)
        return {
            ...(crossLayer ? { crossLayer: true } : {}),
            newAsset: {
                key: nextKey('asset'),
                // A room is named as part of the drawing gesture, so it opens
                // empty with the name field focused.
                name: kind === 'space' ? '' : ctx.localizedTypeLabel(typeCode),
                typeCode,
                parentUid: resolution.parentUid,
                // §2: the containing room may itself be pending — the save
                // creates it first and hands its fresh uid to this asset.
                ...(resolution.parentKey ? { parentKey: resolution.parentKey } : {}),
                parentFallback: resolution.fallback,
                ...(resolution.ontologyRefused ? { parentRefused: true } : {}),
            },
        }
    }, [assetUid, isCrossLayer])

    /* ---- `?center` / `?zoom`: the viewport is part of the link too ---- */

    /**
     * The view the URL asked for **when this page opened**, read once.
     *
     * It has to be a ref: the debounce below rewrites these same parameters as
     * the user pans, and re-reading the props would turn every write into a new
     * instruction to move — the editor would restore its way back to wherever
     * the last commit happened to land instead of following the pointer.
     */
    const openedAtRef = useRef<Partial<PlanViewport> | null>(null)
    if (openedAtRef.current === null) {
        const [cx, cy] = (center ?? '').split(',').map(Number)
        openedAtRef.current = {
            ...(Number.isFinite(cx) && Number.isFinite(cy) ? { cx, cy } : {}),
            ...(zoom ? { k: zoom } : {}),
        }
    }

    /**
     * …and keep it there as it moves, once it has stopped moving.
     *
     * A pan emits a view on every pointer move and a wheel zoom on every notch,
     * so writing each one would put hundreds of entries through the router for
     * one gesture. The timer restarts on each change and only fires when a full
     * second has passed without one, which is the natural end of a gesture.
     * `replace` keeps all of it out of the back button.
     */
    const viewWriteRef = useRef<number | null>(null)
    const onViewChangeRef = useRef<(v: PlanViewport) => void>(() => {})
    useEffect(() => {
        onViewChangeRef.current = (view) => {
            if (viewWriteRef.current) window.clearTimeout(viewWriteRef.current)
            viewWriteRef.current = window.setTimeout(() => {
                viewWriteRef.current = null
                void navigate({
                    to: '.',
                    search: (prev: Record<string, unknown>) => ({
                        ...prev,
                        // Whole millimetres, and a scale to five figures: a plan
                        // is not navigated to the micron, and the extra digits
                        // would only make the link harder to read and to share.
                        center: `${Math.round(view.cx)},${Math.round(view.cy)}`,
                        zoom: Number(view.k.toPrecision(5)),
                    }),
                    replace: true,
                })
            }, VIEW_URL_DEBOUNCE_MS)
        }
    }, [navigate])

    // A pending write must not land on the next page.
    useEffect(() => () => {
        if (viewWriteRef.current) window.clearTimeout(viewWriteRef.current)
    }, [])

    /**
     * `Cmd/Ctrl+S`. Held in a ref because the engine is built once per frame
     * while `onSave` is rebuilt on every render that touches the document.
     */
    const onSaveRef = useRef<() => void>(() => {})

    /**
     * Bumped every time a space is double-clicked (spec 2026-08-30-03 §13).
     * A counter rather than a boolean: double-clicking the *same* shape twice
     * has to put the cursor back in the field both times, and a flag that is
     * already `true` says nothing the second time.
     */
    const [renameNonce, setRenameNonce] = useState(0)

    /* ---- engine lifecycle ---- */

    /**
     * The scene the engine is **built** from: the first payload that arrives for
     * this frame, and a new one only when the frame itself changes.
     *
     * Every later payload — the refetch after a save, after an import, after
     * registering an underlay — reaches the engine through `setDoc` instead.
     * Keying the lifecycle effect on the live query data made React tear the
     * engine down and rebuild it on every refetch, and a rebuilt engine fits the
     * view: saving re-framed the whole drawing and threw away the zoom, along
     * with the selection and the undo history.
     */
    const [engineSeed, setEngineSeed] = useState<{ uid: string; scene: NonNullable<typeof sceneQuery.data> } | null>(null)
    useEffect(() => {
        const data = sceneQuery.data
        if (!data) return
        // Wait for the memberships before seeding: an engine built without them
        // has a baseline in which no space is grouped, and the next save would
        // re-write every existing `group_member` edge as if the user had just made
        // it (§9). An *error* is enough to proceed — the editor still works
        // without the grouping, it just cannot show it.
        if (!relationsQuery.isSuccess && !relationsQuery.isError) return
        // Wait for the asset types too: every frame rule keys on a type code
        // (which assets anchor a frame, which parents a type accepts). Before
        // they arrive, a unit reads as untyped, so re-owning a wall onto it
        // skipped the cross-frame confirmation (spec 2026-08-25-04 §6) on a
        // slow `GET /asset-types` (weekly e2e run, issue #1565).
        if (!assetTypesLoaded && !assetTypesFailed) return
        setEngineSeed((prev) => (prev?.uid === assetUid ? prev : { uid: assetUid, scene: data }))
    }, [sceneQuery.data, assetUid, relationsQuery.isSuccess, relationsQuery.isError,
        assetTypesLoaded, assetTypesFailed])

    useEffect(() => {
        const data = engineSeed?.uid === assetUid ? engineSeed.scene : undefined
        if (!data || !stageEl || engineRef.current) return
        const doc = applyGroupers(sceneFromPlanScene(data))
        baselineRef.current = cloneDoc(doc)
        const engine = new PlanEditor(doc, {
            onChange: (isDirty) => {
                setDirty(isDirty)
                syncFromEngine()
            },
            onSelectionChange: (key, extras) => {
                setSelectedKey(key)
                setSelectionCount(key ? 1 + (extras?.length ?? 0) : 0)
                setSelectedKeys(key ? [key, ...(extras ?? [])] : [])
            },
            // The engine owns the active tool, so the keyboard shortcuts reach
            // the shell through here rather than only through the toolbar.
            onToolChange: (next) => {
                setTool(next)
                if (next !== 'hole') setHoleRejected(false)
            },
            onHoleCut: () => setHoleRejected(false),
            onHoleRejected: () => setHoleRejected(true),
            toolAvailable: (next) => next !== 'unit' || grouperAllowedRef.current,
            onRoomCreated: () => {
                // The panel opens on the field the drawing cannot guess, the
                // moment a room closes — so answering it is part of the
                // gesture. That field is the **type**: a room's identity is
                // what it is, and the server names it from that and its number
                // (spec 2026-08-31-02 §2), so a name is the exception rather
                // than the step. The name is the fallback for an organization
                // whose ontology has no room sub-types — there the select is
                // not rendered at all.
                window.setTimeout(() => {
                    const type = document.getElementById('plan-editor-field-room-type') as HTMLSelectElement | null
                    if (type) {
                        type.focus()
                        return
                    }
                    const input = document.getElementById('plan-editor-field-room-name') as HTMLInputElement | null
                    input?.focus()
                    input?.select()
                }, 30)
            },
            onEquipmentPlaced: () => setPickerOpen(true),
            // Said once per editor session: the cap is a hint, not a nag.
            onFootprintReduced: () => {
                if (footprintNoticeShownRef.current) return
                footprintNoticeShownRef.current = true
                setFootprintNotice(true)
            },
            onEquipmentMoved: (key) => onEquipmentMovedRef.current(key),
            onSaveRequested: () => onSaveRef.current(),
            onRenameRequested: () => setRenameNonce((n) => n + 1),
            // The clipboard outlives this editor: copy on one floor, paste on
            // the next. Session-scoped and **not** keyed by frame, unlike the
            // thickness and size memories, because carrying it across floors is
            // the whole point.
            onClipboardChange: (items) => {
                try {
                    sessionStorage.setItem(CLIPBOARD_KEY, JSON.stringify(items))
                } catch {
                    // Storage is a convenience; the paste still works in-page.
                }
            },
            // Read at the moment of the click, so the drop lands as the armed
            // type with its own footprint instead of an untyped 600×300 box.
            equipmentDefaults: () => ({
                typeCode: armedRef.current?.code,
                width: armedSizeRef.current.width,
                depth: armedSizeRef.current.depth,
            }),
            runDefaults: () => runArmRef.current,
            riserDefaults: () => riserArmRef.current,
            networkLabelOf: (code) => networkLabelRef.current(code),
            createOwner: ownerForCreated,
            wallOwner: () => {
                const owner = ctxRef.current.drawParentUid ?? assetUid
                const target = ctxRef.current.assetIndex.get(owner)
                return {
                    assetUid: owner,
                    assetName: target?.name,
                    assetTypeCode: target?.typeCode,
                    assetParentUid: target?.parentUid,
                }
            },
            wallRepFor: (owner) => {
                // New walls join the target asset's existing wall bundle when it
                // already has one, so a floor keeps a single structure rep.
                const current = engineRef.current?.getDoc()
                if (!current || !owner.assetUid) return null
                // A run joins the same bundle — or, on a networks-only layer
                // with no wall yet, the bundle its sibling runs already live in.
                const existing = current.elements.find(
                    (el) => el.kind === 'wall' && el.source.repUid && el.owner.assetUid === owner.assetUid,
                ) ?? current.elements.find(
                    (el) => (el.kind === 'run' || el.kind === 'riser') && el.source.repUid && el.source.repOwner?.assetUid === owner.assetUid,
                )
                if (!existing) return null
                return {
                    repUid: existing.source.repUid,
                    registrationUid: existing.source.registrationUid,
                    transform: existing.source.transform,
                }
            },
            underlayHref: (repUid) => authedRenderUrl(org, repUid),
            // Through the ref, like everything else the engine reads late: the
            // catalog lands after the engine is built, and rebuilding it when a
            // label changes would throw away the drawing.
            typeLabelOf: (code) => ctxRef.current.localizedTypeLabel(code),
            onCalibrationChange: (state) => setCalibration(state),
            onMeasureReady: (a, b) => {
                setMeasureDraft('1000')
                setMeasureRequest({ a, b })
            },
            onLayerSelectionChange: (uid) => setSelectedLayerUid(uid),
            onCalibrationCommit: () => calibrateCommitRef.current(),
            onViewChange: (view) => onViewChangeRef.current(view),
        })
        engineRef.current = engine
        // Anything copied earlier in the session, including on another floor.
        try {
            const raw = sessionStorage.getItem(CLIPBOARD_KEY)
            const parsed: unknown = raw ? JSON.parse(raw) : null
            if (Array.isArray(parsed) && parsed.length) engine.setClipboard(parsed as ClipboardItem[])
        } catch {
            // An unreadable clipboard is simply an empty one.
        }
        engine.mount(stageEl)
        engine.setHiddenNetworks(hiddenNetworksRef.current)
        // Fit first, always: it is the right answer with no link to honour, and
        // it is the floor a half-given one falls back to (a `zoom` with no
        // `center` keeps the fitted centre, and vice versa).
        engine.fitView()
        const opening = openedAtRef.current
        if (opening && Object.keys(opening).length) engine.setViewport(opening)
        syncFromEngine()
        return () => {
            engine.destroy()
            engineRef.current = null
        }
    }, [engineSeed, assetUid, stageEl, org, ownerForCreated, syncFromEngine, applyGroupers])

    /**
     * Lengths the user thinks in (millimetres) against the frame's plan units.
     * Geometry stays in plan units; a millimetre habit or field is converted
     * at the edge, through the engine that knows the scale and the zoom.
     */
    const mmToUnits = useCallback((mm: number) => engineRef.current?.mmToUnits(mm) ?? mm, [])
    const unitsToMm = useCallback((units: number) => engineRef.current?.unitsToMm(units) ?? units, [])
    /** A footprint in mm as plan units, capped to the drawing (the engine tells us when it bites). */
    const footprintToUnits = useCallback(
        (size: EquipmentSize) => engineRef.current?.footprintToUnits(size.width, size.depth)
            ?? { width: size.width, depth: size.depth },
        [],
    )

    /**
     * Picking a type arms its own footprint — unless this plan has already used
     * another size for that type, which is the better guess by definition.
     */
    const onArmEquipmentType = useCallback((picked: PickedEquipmentType) => {
        setArmedEquipment(picked)
        const remembered = equipmentSizes.recentFor(picked.code)[0]
        const size = remembered ?? { width: picked.widthMm ?? 600, depth: picked.depthMm ?? 300 }
        setArmedSize(size)
        equipmentSizes.remember(picked.code, size)
    }, [equipmentSizes])

    const onArmEquipmentSize = useCallback((size: EquipmentSize) => {
        setArmedSize(size)
        equipmentSizes.remember(armedEquipment?.code, size)
    }, [equipmentSizes, armedEquipment])

    /**
     * Does the selection hold more than one asset type? Only equipment and
     * openings carry one that the inspector edits, so nothing else can make a
     * group "mixed".
     */
    const mixedTypes = useMemo(() => {
        if (!docState || selectedKeys.length < 2) return false
        const types = new Set<string>()
        for (const key of selectedKeys) {
            const el = elementByKey(docState.doc, key)
            if (!el) continue
            if (el.kind === 'equipment') types.add(el.typeCode ?? '')
            else if (el.kind === 'opening') {
                types.add(el.owner.newAsset?.typeCode ?? el.owner.assetTypeCode ?? '')
            } else return false
        }
        return types.size > 1
    }, [docState, selectedKeys])

    /** Set a placed equipment's footprint, and remember it for its type. */
    const onSelectedEquipmentSize = useCallback((size: EquipmentSize) => {
        if (!selectedKey) return
        // The footprint is written to every equipment in the group, but it is
        // remembered against the PRIMARY's type only: on a mixed group the
        // iteration order is arbitrary, so keying the memory off the last
        // element filed the size under whichever type happened to come last.
        let primaryTypeCode: string | undefined
        engineRef.current?.updateAllSelected((el) => {
            if (el.kind !== 'equipment') return
            const fit = footprintToUnits(size)
            el.width = fit.width
            el.depth = fit.depth
            if (el.key === selectedKey) primaryTypeCode = el.typeCode
        })
        equipmentSizes.remember(primaryTypeCode, size)
    }, [selectedKey, equipmentSizes, footprintToUnits])

    /**
     * A pick from the sizes remembered **for the primary's type**. On a mixed
     * group that value belongs to one member only, so it lands on the primary
     * alone (spec 2026-09-06-01 §1); with one type throughout it is a group
     * edit like every other footprint change.
     */
    const onSelectedEquipmentSizePreset = useCallback((size: EquipmentSize) => {
        if (!mixedTypes) {
            onSelectedEquipmentSize(size)
            return
        }
        const engine = engineRef.current
        if (!engine || !selectedKey) return
        let typeCode: string | undefined
        engine.updateElement(selectedKey, (el) => {
            if (el.kind !== 'equipment') return
            const fit = footprintToUnits(size)
            el.width = fit.width
            el.depth = fit.depth
            typeCode = el.typeCode
        })
        equipmentSizes.remember(typeCode, size)
    }, [mixedTypes, onSelectedEquipmentSize, selectedKey, equipmentSizes, footprintToUnits])

    /** Arm the next wall, and remember the value for the rest of the session. */
    const rememberThickness = wallThickness.remember
    const applyWallThickness = useCallback((mm: number) => {
        engineRef.current?.setWallThickness(mm)
        rememberThickness(mm)
    }, [rememberThickness])

    // The engine owns the sticky value, React owns the memory of it. This
    // re-syncs them on both — and keys on `engineSeed`, the same signal the
    // lifecycle effect above builds the engine from, so a freshly built engine
    // is armed in that very commit rather than a render later.
    useEffect(() => {
        engineRef.current?.setWallThickness(wallThickness.thickness)
    }, [wallThickness.thickness, stageEl, engineSeed])

    // `?calibrate=<uid>`: the frame registrations panel hands the editor a
    // registration to calibrate (spec §6). Fired once, after the scene is in.
    const deepLinkedRef = useRef(false)
    useEffect(() => {
        const engine = engineRef.current
        if (!engine || !calibrateRegistrationUid || deepLinkedRef.current || !docState) return
        deepLinkedRef.current = true
        engine.beginCalibration(calibrateRegistrationUid)
        setTool('calibrate')
    }, [calibrateRegistrationUid, docState])

    /* ---- `?focus=<assetUid>`: a link to one element of the plan ---- */

    // Restore first, and exactly once: the sync below would otherwise strip the
    // incoming parameter on the first commit, before anything had read it.
    const focusRestoredRef = useRef(false)
    useEffect(() => {
        if (focusRestoredRef.current || !docState) return
        const target = focusAssetUid
            ? docState.doc.elements.find((el) => el.owner.assetUid === focusAssetUid)
            : undefined
        // An asset with no drawing of its own (a lot opened on its floor's plan)
        // is located by what is drawn under it, which takes the hierarchy.
        if (focusAssetUid && !target && descendantsQuery.isPending) return
        focusRestoredRef.current = true
        if (!focusAssetUid) return
        if (target) {
            engineRef.current?.select(target.key)
            engineRef.current?.panTo(target.key)
            return
        }
        // A viewport in the link wins: it is where the reader left off.
        if (Object.keys(openedAtRef.current ?? {}).length) return
        const under = (uid: string | undefined) => {
            for (let u = uid, hops = 0; u && hops < 32; u = assetIndex.get(u)?.parentUid, hops++) {
                if (u === focusAssetUid) return true
            }
            return false
        }
        const keys = docState.doc.elements
            .filter((el) => el.kind !== 'run' && el.kind !== 'riser' && under(el.owner.assetUid))
            .map((el) => el.key)
        if (keys.length) engineRef.current?.fitElements(keys)
    }, [focusAssetUid, docState, descendantsQuery.isPending, assetIndex])

    // …then keep the URL pointing at whatever is selected. Only elements that
    // *depict a saved asset* qualify: a wall belongs to a bundle owned by the
    // frame asset itself, and "focus the floor" would reopen on whichever wall
    // happened to come first. `replace` keeps a click from growing history.
    // A run does not qualify either: it depicts a network asset that dozens of
    // runs share, so the link would reopen on whichever came first.
    const focusedElement = selectedKey && docState ? elementByKey(docState.doc, selectedKey) : undefined
    const focusableUid = focusedElement?.kind === 'run' || focusedElement?.kind === 'riser' ? undefined : focusedElement?.owner.assetUid
    const focusInUrl = focusableUid && focusableUid !== assetUid ? focusableUid : undefined
    useEffect(() => {
        if (!focusRestoredRef.current) return
        void navigate({
            to: '.',
            search: (prev: Record<string, unknown>) => ({ ...prev, focus: focusInUrl }),
            replace: true,
        })
    }, [focusInUrl, navigate])

    // A run depicts a network asset parented at the building or the site —
    // outside the frame's subtree, so no list above names it. The uids the
    // runs point at are fetched once, and land in the same index a linked
    // asset does.
    const runOwnerUids = useMemo(() => {
        const uids = new Set<string>()
        for (const el of docState?.doc.elements ?? []) {
            const uid = el.kind === 'run' || el.kind === 'riser' ? el.owner.assetUid : undefined
            if (uid && uid !== el.source.repOwner?.assetUid && !assetIndex.has(uid)) uids.add(uid)
        }
        return [...uids].sort()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [docState?.doc, docState?.version, assetIndex])
    const runOwnersQuery = $api.useQuery('get', ASSETS_PATH, {
        params: { path: { orgCode: org }, query: { uid: runOwnerUids, limit: 100 } },
    }, { enabled: runOwnerUids.length > 0 })
    useEffect(() => {
        const rows = (runOwnersQuery.data?.data ?? []) as unknown as RawAsset[]
        if (!rows.length) return
        setLinkedAssets((prev) => {
            const missing = rows.filter((r) => r.uid && !prev[r.uid])
            if (!missing.length) return prev
            const next = { ...prev }
            for (const r of missing) next[r.uid] = r
            return next
        })
    }, [runOwnersQuery.data])

    // Bundles depict assets the scene payload does not name; the shell's asset
    // index is what turns those uids into labels (spec §4).
    useEffect(() => {
        const engine = engineRef.current
        if (!engine || !assetIndex.size) return
        if (hydrateOwnerNames(engine.getDoc(), (uid) => assetIndex.get(uid))) syncFromEngine()
    }, [assetIndex, docState?.version, syncFromEngine])

    // Browser-level guard; the in-app one is the blocker below.
    useEffect(() => {
        if (!dirty) return
        const handler = (e: BeforeUnloadEvent) => {
            e.preventDefault()
            e.returnValue = ''
        }
        window.addEventListener('beforeunload', handler)
        return () => window.removeEventListener('beforeunload', handler)
    }, [dirty])

    const blocker = useBlocker({
        // Only *leaving* counts. The editor rewrites its own `?focus` as the
        // selection moves, and a search-only navigation must not raise the
        // unsaved-changes guard on every click.
        shouldBlockFn: ({ current, next }) => dirty && next.pathname !== current.pathname,
        withResolver: true,
        disabled: !dirty,
    })
    useEffect(() => {
        if (blocker.status === 'blocked') {
            setPendingLeave({ proceed: blocker.proceed, reset: blocker.reset })
        }
    }, [blocker.status, blocker.proceed, blocker.reset])

    useBreadcrumbs([
        { title: t('common:nav.home', 'Home'), path: `/${org}` },
        { title: asset?.name ?? assetUid, path: `/${org}/assets/${assetUid}` },
        ...(subject ? [{ title: subject.name, path: `/${org}/assets/${subject.uid}` }] : []),
        { title: t('planEditor.title') },
    ])

    useEffect(() => {
        document.title = `${t('planEditor.title')} — ${asset?.name ?? ''}`
        return () => {
            document.title = 'Buildtory'
        }
    }, [asset?.name, t])

    /* ---- toolbar / panel handlers ---- */

    const onUndo = useCallback(() => engineRef.current?.undo(), [])
    const onRedo = useCallback(() => engineRef.current?.redo(), [])
    const onFit = useCallback(() => engineRef.current?.fitView(), [])
    const onZoomIn = useCallback(() => engineRef.current?.zoomBy(1.3), [])
    const onZoomOut = useCallback(() => engineRef.current?.zoomBy(1 / 1.3), [])
    const onDeleteSelected = useCallback(() => engineRef.current?.deleteSelected(), [])
    const onDuplicateSelected = useCallback(() => engineRef.current?.duplicateSelected(), [])

    const onLayerChange = useCallback((id: LayerId, patch: Partial<{ on: boolean; opacity: number }>) => {
        engineRef.current?.setLayer(id, patch)
        setLayers((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }))
    }, [])

    const onUnderlayChange = useCallback((key: string, patch: Partial<{ visible: boolean; opacity: number }>) => {
        const engine = engineRef.current
        if (!engine) return
        engine.setUnderlay(key, patch)
        setDocState((prev) => ({ doc: engine.getDoc(), version: (prev?.version ?? 0) + 1 }))
    }, [])

    /**
     * A row click. Plain replaces the selection and brings it into view;
     * Shift extends the group instead and deliberately does **not** pan — the
     * canvas rule, so building a group from the panel does not throw the view
     * around (spec 2026-09-06-01 §1).
     */
    const selectAndPan = useCallback((key: string, opts?: { extend?: boolean }) => {
        if (opts?.extend) {
            engineRef.current?.toggleSelect(key)
            return
        }
        engineRef.current?.select(key)
        engineRef.current?.panTo(key)
    }, [])

    const selectedElement: EditorElement | null = useMemo(
        () => (docState ? elementByKey(docState.doc, selectedKey) ?? null : null),
        [selectedKey, docState],
    )

    // Applies to **every** selected element: the inspector shows the primary's
    // values, and a shift-built group is edited through them.
    const onUpdateSelected = useCallback((patch: (el: EditorElement) => void) => {
        if (!selectedKey) return
        engineRef.current?.updateAllSelected(patch)
    }, [selectedKey])

    /**
     * The primary alone. A category group can hold several asset types — a
     * radiator and a boiler, an entry door and an interior one — and the
     * fields those do not share in meaning go through here rather than
     * through `updateAllSelected` (spec 2026-09-06-01 §1).
     */
    const onUpdatePrimary = useCallback((patch: (el: EditorElement) => void) => {
        if (!selectedKey) return
        engineRef.current?.updateElement(selectedKey, patch)
    }, [selectedKey])


    /** Editing a wall's thickness also arms the next one with it. */
    const onSelectedWallThickness = useCallback((mm: number) => {
        if (!selectedKey) return
        engineRef.current?.updateAllSelected((el) => {
            if (el.kind === 'wall') el.thickness = mmToUnits(mm)
        })
        applyWallThickness(mm)
    }, [selectedKey, applyWallThickness, mmToUnits])

    /**
     * Attach the geometry to an existing asset.
     *
     * This **never reparents** (spec 2026-08-25-04 §5): linking a radiator
     * drawn on floor 3 to the building's elevator adds a footprint and nothing
     * else. When the linked asset's implicit frame is somewhere else, that
     * footprint is a cross-layer one and its representation is the rare case
     * that still earns an explicit registration row (§1).
     */
    const onLinkAsset = useCallback((
        linkedUid: string,
        linkedName: string,
        record?: { asset_type_uid?: string; asset_type_code?: string; parent_asset_uid?: string },
    ) => {
        if (!selectedKey) return
        const key = selectedKey
        const known = ctxRef.current.assetIndex.get(linkedUid)
        const typeCode = known?.typeCode
            ?? record?.asset_type_code
            ?? (record?.asset_type_uid ? assetTypeByUid.get(record.asset_type_uid)?.code : undefined)
        engineRef.current?.updateElement(key, (el) => {
            el.owner = {
                assetUid: linkedUid,
                assetName: linkedName,
                assetTypeCode: typeCode,
                assetParentUid: known?.parentUid ?? record?.parent_asset_uid,
            }
        })
        // A newly linked asset may well be drawn elsewhere — re-resolve.
        sharedRequestedRef.current.delete(linkedUid)
        syncFromEngine()

        // Is this a cross-layer footprint? Only the asset's **parent chain**
        // can say, and the asset search does not return it — a linked asset
        // lives outside this frame's subtree by definition, so nothing has
        // indexed it yet. One read settles it, and only then does the save
        // write the explicit registration row (spec 2026-08-25-04 §1).
        void (async () => {
            let linked: AssetLite = {
                uid: linkedUid,
                name: linkedName,
                typeCode,
                parentUid: known?.parentUid ?? record?.parent_asset_uid,
            }
            if (!linked.parentUid) {
                try {
                    const fetched = await request(ASSET_PATH, 'GET', {
                        params: { path: { orgCode: org, assetUid: linkedUid } },
                    }) as RawAsset | undefined
                    if (fetched?.uid) {
                        setLinkedAssets((prev) => ({ ...prev, [linkedUid]: fetched }))
                        linked = {
                            uid: linkedUid,
                            name: fetched.name ?? linkedName,
                            typeCode: fetched.asset_type_uid ? assetTypeByUid.get(fetched.asset_type_uid)?.code : typeCode,
                            parentUid: fetched.parent_asset_uid ?? undefined,
                        }
                    }
                } catch {
                    // Unreadable: trust the implicit rule rather than writing
                    // a registration row nothing asked for.
                }
            }
            const index = new Map(ctxRef.current.assetIndex)
            index.set(linkedUid, linked)
            if (!needsExplicitRegistration(linkedUid, index, assetUid)) return
            engineRef.current?.updateElement(key, (el) => {
                if (el.owner.assetUid !== linkedUid) return
                el.owner = { ...el.owner, assetParentUid: linked.parentUid, crossLayer: true }
            })
            syncFromEngine()
        })()
    }, [selectedKey, assetUid, assetTypeByUid, org, request, syncFromEngine])

    const onPickEquipmentType = useCallback((picked: PickedEquipmentType) => {
        if (!selectedKey) return
        const engine = engineRef.current
        if (!engine) return
        const element = elementByKey(engine.getDoc(), selectedKey)
        const position = element?.kind === 'equipment' ? element.position : { x: 0, y: 0 }
        const owner = ownerForCreated('equipment', position, { typeCode: picked.code })
        engine.updateElement(selectedKey, (el) => {
            if (el.kind !== 'equipment') return
            el.typeCode = picked.code
            if (picked.widthMm || picked.depthMm) {
                const fit = footprintToUnits({
                    width: picked.widthMm ?? unitsToMm(el.width),
                    depth: picked.depthMm ?? unitsToMm(el.depth),
                })
                if (picked.widthMm) el.width = fit.width
                if (picked.depthMm) el.depth = fit.depth
            }
            // Only a pending asset takes the picked type: an existing asset's
            // type is inventory, not geometry, and this editor never retypes one.
            if (!el.owner.assetUid) {
                el.owner = { ...owner, newAsset: owner.newAsset ? { ...owner.newAsset, name: picked.label } : undefined }
            }
        })
    }, [selectedKey, ownerForCreated, footprintToUnits, unitsToMm])

    /* ---- calibration, measure and layer manipulation (spec 03 §1/§2/§6) ---- */

    const sceneLayerList = useMemo(
        () => (docState ? sceneLayers(docState.doc) : []),
        [docState],
    )

    /**
     * The frame's millimetres-per-plan-unit (§3). `null` is a valid state — the
     * frame is not to scale — and every readout says so rather than presenting
     * nominal plan units as measurements.
     */
    const frameMmPerUnit = useMemo(
        () => (docState ? sceneMmPerUnit(docState.doc) : null),
        [docState],
    )

    /* ---- shared assets across layers (spec 2026-08-25-04 §5) ---- */

    // assetUid → every layer that asset is drawn on. Resolved lazily, on
    // selection: the scene payload only knows about *this* frame, and fanning
    // out over sibling frames on every load would cost a request per floor for
    // information almost nobody looks at.
    const [sharedLayers, setSharedLayers] = useState<Record<string, SharedLayer[]>>({})
    const [sharedLoading, setSharedLoading] = useState(false)
    const sharedRequestedRef = useRef<Set<string>>(new Set())

    const resolveSharedLayers = useCallback(async (targetUid: string): Promise<void> => {
        const engine = engineRef.current
        if (!engine) return
        const doc = engine.getDoc()
        const local = assetFootprints(doc).get(targetUid) ?? []
        const here: SharedLayer[] = local.map((f) => ({
            repUid: f.repUid,
            frameUid: doc.frame.uid,
            frameName: doc.frame.name || doc.frame.uid,
            current: true,
        }))
        setSharedLoading(true)
        try {
            const repsResponse = await request(REPRESENTATIONS_PATH, 'GET', {
                params: { path: { orgCode: org, assetUid: targetUid } },
            })
            const reps = ((repsResponse as { representations?: { uid: string; kind: string }[] })?.representations ?? [])
                .filter((rep) => rep.kind === 'vector')
            const localRepUids = new Set(here.map((l) => l.repUid))
            const elsewhere = reps.map((r) => r.uid).filter((uid) => !localRepUids.has(uid))
            if (!elsewhere.length) {
                setSharedLayers((prev) => ({ ...prev, [targetUid]: here }))
                return
            }

            // The other layers are sibling frames: the floors of this floor's
            // building (plus the building itself). One registrations call each,
            // capped — a cross-layer row names its target frame, so a match
            // there *is* the layer.
            const frameAsset = assetIndex.get(assetUid)
            const candidates: AssetLite[] = []
            if (frameAsset?.parentUid) {
                const siblings = await request(ASSETS_PATH, 'GET', {
                    params: { path: { orgCode: org }, query: { parent_asset_uid: frameAsset.parentUid, limit: '200' } },
                })
                for (const a of ((siblings as { data?: { uid: string; name?: string; asset_type_uid?: string }[] })?.data ?? [])) {
                    if (a.uid === assetUid) continue
                    const typeCode = a.asset_type_uid ? assetTypeByUid.get(a.asset_type_uid)?.code : undefined
                    if (typeCode && !['floor', 'unit', 'building', 'building_group'].includes(lotBaseTypeCode(typeCode))) continue
                    candidates.push({ uid: a.uid, name: a.name, typeCode })
                }
            }

            const found: SharedLayer[] = []
            for (const candidate of candidates.slice(0, 20)) {
                try {
                    const rows = await request(REGISTRATIONS_PATH, 'GET', {
                        params: { path: { orgCode: org, assetUid: candidate.uid } },
                    })
                    for (const row of ((rows as { registrations?: { source_representation_uid?: string | null }[] })?.registrations ?? [])) {
                        const repUid = row.source_representation_uid ?? undefined
                        if (!repUid || !elsewhere.includes(repUid)) continue
                        found.push({
                            repUid,
                            frameUid: candidate.uid,
                            frameName: candidate.name ?? candidate.uid,
                            current: false,
                        })
                    }
                } catch {
                    // A frame we cannot read simply contributes no layer.
                }
            }
            setSharedLayers((prev) => ({ ...prev, [targetUid]: [...here, ...found] }))
        } catch {
            setSharedLayers((prev) => ({ ...prev, [targetUid]: here }))
        } finally {
            setSharedLoading(false)
        }
    }, [org, assetUid, request, assetIndex, assetTypeByUid])

    const selectedOwnerUid = selectedElement?.owner.assetUid
    useEffect(() => {
        if (!selectedOwnerUid || sharedRequestedRef.current.has(selectedOwnerUid)) return
        sharedRequestedRef.current.add(selectedOwnerUid)
        void resolveSharedLayers(selectedOwnerUid)
    }, [selectedOwnerUid, resolveSharedLayers])

    /**
     * Assets whose footprint lives on another layer too (§5). Deleting the
     * drawing here is not "no longer drawn anywhere", so they never reach the
     * post-save orphan list.
     */
    const drawnElsewhere = useMemo(() => {
        const out = new Set<string>()
        for (const [uid, layers] of Object.entries(sharedLayers)) {
            if (layers.some((l) => !l.current)) out.add(uid)
        }
        return out
    }, [sharedLayers])

    const selectedSharedLayers = useMemo(
        () => (selectedOwnerUid ? sharedLayers[selectedOwnerUid] ?? [] : []),
        [selectedOwnerUid, sharedLayers],
    )

    const onOpenLayer = useCallback((frameUid: string) => {
        void navigate({ to: '/$org/assets/$assetUid/plan-editor', params: { org, assetUid: frameUid } })
    }, [navigate, org])

    /* ---- resolved parent + override (spec 2026-08-25-04 §2 / §6) ---- */

    const selectedParent = useMemo((): { uid?: string; name: string } | null => {
        const el = selectedElement
        if (!el) return null
        const pending = el.owner.newAsset
        if (pending) {
            if (pending.parentKey) {
                const host = docState?.doc.elements.find((e) => e.owner.newAsset?.key === pending.parentKey)
                const name = host?.owner.newAsset?.name || (host?.kind === 'space' ? host.name : '')
                return { name: name || t('planEditor.untitled') }
            }
            const parent = pending.parentUid ? assetIndex.get(pending.parentUid) : undefined
            return { uid: pending.parentUid, name: parent?.name ?? pending.parentUid ?? '' }
        }
        const parentUid = el.owner.assetParentUid
        if (!parentUid) return null
        return { uid: parentUid, name: assetIndex.get(parentUid)?.name ?? parentUid }
    }, [selectedElement, docState, assetIndex, t])

    /** Re-parent everything selected — a shift-built group moves together. */
    const onParentChange = useCallback((parentUid: string) => {
        const engine = engineRef.current
        if (!engine || !selectedKey) return
        const doc = engine.getDoc()
        engine.updateAllSelected((el) => {
            // A pending asset takes the override directly; an existing one is a
            // real re-parent, applied to every element that depicts it.
            if (overridePendingParent(el, parentUid)) return
            if (el.owner.assetUid) overrideExistingParent(doc, el.owner.assetUid, parentUid)
        })
        syncFromEngine()
    }, [selectedKey, syncFromEngine])

    /** Link a pending element to a pending room — the room drawn moments ago. */
    const onParentKeyChange = useCallback((parentKey: string) => {
        const engine = engineRef.current
        if (!engine || !selectedKey) return
        const host = engine.getDoc().elements.find((el) => el.owner.newAsset?.key === parentKey)
        engine.updateAllSelected((el) => {
            overridePendingParentKey(el, parentKey, host?.owner.newAsset?.parentUid)
        })
        syncFromEngine()
    }, [selectedKey, syncFromEngine])

    /* ---- grouping: assign, create inline (spec 2026-08-30-01 §5) ---- */

    /**
     * Grouping failures from the last save, localized and keyed to what the
     * user can act on — the field, not the summary strip (spec 2026-08-30-01,
     * `## Verification`). Two shapes reach it:
     *
     *   - the pending grouper that could not be created, by its key. That is
     *     the duplicate-name 409, and the message belongs under the combobox
     *     the name was typed into;
     *   - the space whose `group_member` edge was refused, by its uid or
     *     pending key. `group_member` is declared `source_cardinality: "one"`,
     *     so a space already grouped elsewhere — under a grouper outside this
     *     frame, or one of a type the combobox does not offer — cannot also be
     *     filed under a lot until that edge goes. Nothing in the editor can fix
     *     that for them, so the least it can do is say what happened instead of
     *     showing "relation cardinality violated". A `part_of` edge is no
     *     longer in the way: physical composition and grouping now hold
     *     separate slots.
     */
    const [grouperErrors, setGrouperErrors] = useState<{
        byGrouperKey: Record<string, string>
        bySpace: Record<string, string>
    }>({ byGrouperKey: {}, bySpace: {} })
    /** The Code field's own message — the org-wide external-id clash (§3). */
    const [codeError, setCodeError] = useState<string | null>(null)
    const clearGrouperErrors = useCallback(() => {
        setCodeError(null)
        setGrouperErrors((prev) =>
            Object.keys(prev.byGrouperKey).length || Object.keys(prev.bySpace).length
                ? { byGrouperKey: {}, bySpace: {} }
                : prev)
    }, [])

    const onAssignGrouper = useCallback((ref: GrouperRef | null) => {
        clearGrouperErrors()
        engineRef.current?.assignGrouper(ref)
        // A `null` ref is the "clear" action, not a pick (spec 2026-09-06-06)
        // — it never touches the recent-groupers memory.
        const recentUid = ref?.assetUid ?? (ref?.assetKey ? `pending:${ref.assetKey}` : undefined)
        if (recentUid) onGrouperPicked(recentUid)
        syncFromEngine()
    }, [syncFromEngine, clearGrouperErrors, onGrouperPicked])

    /**
     * Create a grouper from the combobox and file the selection under it in the
     * same gesture. The asset does not exist yet: it goes into the document's
     * `pendingGroupers`, the save materializes it before the edges that point
     * at it, and a grouper nothing ends up pointing at is never created at all.
     *
     * The tree parent is the frame being edited (§4) — a lot belongs to the
     * floor it was drawn on, and the combobox never re-parents anything else.
     */
    const onCreateGrouper = useCallback((name: string, typeCode: string) => {
        const engine = engineRef.current
        if (!engine) return
        clearGrouperErrors()
        const trimmed = name.trim()
        if (!trimmed) return
        const key = nextKey('asset')
        const doc = engine.getDoc()
        doc.pendingGroupers = {
            ...(doc.pendingGroupers ?? {}),
            [key]: { key, name: trimmed, typeCode, parentUid: assetUid, parentFallback: false },
        }
        engine.assignGrouper({ assetKey: key, name: trimmed, typeCode })
        onGrouperPicked(`pending:${key}`)
        syncFromEngine()
    }, [assetUid, syncFromEngine, clearGrouperErrors, onGrouperPicked])

    /**
     * What the combobox offers: the saved units and common areas of the
     * building group, plus the ones created in this session and not yet saved
     * — otherwise a grouper made moments ago could not be reused for the next
     * room. Pending entries are keyed `pending:<key>` so the field can tell the
     * two apart when it hands one back.
     */
    const grouperOptionsWithPending: GrouperOption[] = useMemo(() => {
        const pending = Object.values(docState?.doc.pendingGroupers ?? {})
        const drawn = (docState?.doc.elements ?? [])
            .filter((el) => el.kind === 'space' && isGrouperElement(el) && !el.owner.assetUid && el.owner.newAsset?.name)
            .map((el) => el.owner.newAsset as NonNullable<typeof el.owner.newAsset>)
        return [
            ...grouperOptions,
            ...[...pending, ...drawn].map((a) => ({
                uid: `pending:${a.key}`,
                name: a.name,
                typeCode: a.typeCode,
            })),
        ]
    }, [grouperOptions, docState])

    /** The grouping error to show under the selected space's field, if any. */
    const selectedGrouperError = useMemo(() => {
        const el = selectedElement
        if (!el || el.kind !== 'space') return undefined
        const pendingKey = el.owner.grouper?.assetKey
        if (pendingKey && grouperErrors.byGrouperKey[pendingKey]) return grouperErrors.byGrouperKey[pendingKey]
        const spaceKey = el.owner.assetUid ?? el.owner.newAsset?.key
        return spaceKey ? grouperErrors.bySpace[spaceKey] : undefined
    }, [selectedElement, grouperErrors])

    /* ---- moved into another room: offer to file it there ---- */

    /**
     * Set when a dragged equipment lands in a space that is not the one it is
     * filed under. Dropping something in a room is the moment to ask where it
     * belongs — later, nobody goes looking for the field.
     *
     * `null` for "nothing to suggest", which is also what dismissing it means:
     * the answer is no, and the same drag must not ask twice.
     */
    const [parentSuggestion, setParentSuggestion] = useState<
        { elementKey: string; parentUid?: string; parentKey?: string; name: string } | null
    >(null)

    const onEquipmentMoved = useCallback((key: string) => {
        const engine = engineRef.current
        if (!engine) return
        const doc = engine.getDoc()
        const el = elementByKey(doc, key)
        if (el?.kind !== 'equipment') return
        const space = containingSpace(doc, el.position)
        if (!space) {
            setParentSuggestion(null)
            return
        }
        // A room drawn moments ago has no uid yet, so it is offered by key —
        // the save creates it first and hands the fresh uid down (§2). Only a
        // *pending* element can take that link; an existing asset's re-parent
        // is a PATCH, and a PATCH needs a uid.
        const spaceUid = space.owner.assetUid
        const spaceKey = space.owner.newAsset?.key
        const pending = el.owner.newAsset
        const name = space.owner.assetName
            ?? (spaceUid ? ctxRef.current.assetIndex.get(spaceUid)?.name : undefined)
            ?? space.name
            ?? space.owner.newAsset?.name
            ?? ''
        if (spaceUid) {
            const current = pending?.parentUid ?? el.owner.assetParentUid
            if (current === spaceUid) {
                setParentSuggestion(null)
                return
            }
            setParentSuggestion({ elementKey: key, parentUid: spaceUid, name: name || spaceUid })
            return
        }
        if (!spaceKey || !pending || pending.parentKey === spaceKey) {
            setParentSuggestion(null)
            return
        }
        setParentSuggestion({ elementKey: key, parentKey: spaceKey, name: name || t('planEditor.untitled') })
    }, [t])

    // The suggestion belongs to one element and one drop; selecting something
    // else, or moving on, retires it.
    useEffect(() => {
        if (parentSuggestion && parentSuggestion.elementKey !== selectedKey) setParentSuggestion(null)
    }, [selectedKey, parentSuggestion])

    useEffect(() => {
        onEquipmentMovedRef.current = onEquipmentMoved
    }, [onEquipmentMoved])

    /**
     * Enter / "Apply": the calibration is settled, so the registration is
     * written **now** rather than waiting for Save — it is a deliberate act on
     * an existing row and nothing else in the document depends on it (§1).
     */
    /**
     * A typed "1:N" scale (spec 2026-10-16-06). The page's printed width is
     * its rendered width over the render density, read off the same render
     * the editor draws; the result is a pending calibration, saved by Apply.
     */
    const onStatedScale = useCallback(async (denominator: number) => {
        const engine = engineRef.current
        const layer = sceneLayerList.find((l) => l.registrationUid === calibration.registrationUid)
        if (!engine || !layer?.repUid || layer.sourceKind !== 'pdf') return
        setCalibrateError(null)
        const dpi = 150
        try {
            const width = await new Promise<number>((resolve, reject) => {
                const img = new Image()
                img.onload = () => resolve(img.naturalWidth)
                img.onerror = () => reject(new Error('render'))
                img.src = authedRenderUrl(org, layer.repUid as string, dpi)
            })
            const next = calibrationFromStatedScale(denominator, (width * 25.4) / dpi, layer.transform.scale)
            if (!next) throw new Error('scale')
            engine.applyStatedScale(next)
            syncFromEngine()
        } catch {
            setCalibrateError(t('planEditor.calibrate_scale_error'))
        }
    }, [sceneLayerList, calibration.registrationUid, org, syncFromEngine, t])

    const onCalibrateApply = useCallback(async () => {
        const engine = engineRef.current
        if (!engine) return
        const pending = engine.finishCalibration()
        if (!pending) return
        const doc = engine.getDoc()
        setCalibrateApplying(true)
        setCalibrateError(null)
        try {
            if (pending.kind === 'calibration') {
                // §3: a `calibration` PATCH. No transform travels with it —
                // the underlay and the geometry do not move by a millimetre.
                await writeCalibration(pending.registrationUid, pending.calibration)
                clearCalibrationEdit(doc)
            } else {
                const edit = doc.registrationEdits[pending.registrationUid]
                if (!edit) return
                await writeRegistration(edit)
                clearRegistrationEdit(doc, pending.registrationUid)
            }
            const baseline = baselineRef.current
            if (baseline && isEmptyDiff(computeDiff(baseline, doc))) engine.markSaved()
            invalidate()
            invalidateRegistrations()
        } catch (err) {
            setCalibrateError(t('planEditor.calibrate_error', {
                message: (err as { message?: string })?.message ?? String(err),
            }))
        } finally {
            setCalibrateApplying(false)
            syncFromEngine()
        }
    }, [writeRegistration, writeCalibration, invalidate, invalidateRegistrations, syncFromEngine, t])

    // The engine calls this on Enter; a ref keeps the hook stable across renders.
    const calibrateCommitRef = useRef(onCalibrateApply)
    useEffect(() => {
        calibrateCommitRef.current = onCalibrateApply
    }, [onCalibrateApply])

    const onCalibrateCancel = useCallback(() => {
        const engine = engineRef.current
        if (!engine) return
        setCalibrateError(null)
        engine.cancelCalibration()
        engine.setTool('select')
        setTool('select')
        syncFromEngine()
    }, [syncFromEngine])

    const onMeasureConfirm = useCallback(() => {
        const engine = engineRef.current
        const request = measureRequest
        if (!engine || !request) return
        const value = Number(measureDraft)
        if (Number.isFinite(value) && value > 0) engine.applyMeasure(request.a, request.b, value)
        setMeasureRequest(null)
        syncFromEngine()
    }, [measureRequest, measureDraft, syncFromEngine])

    const onCalibrateLayer = useCallback((registrationUid: string) => {
        engineRef.current?.beginCalibration(registrationUid)
        setTool('calibrate')
    }, [])

    /* ---- §3: calibrating a floor creates the layer it needs ---- */

    /**
     * The registration the frame's calibration lives on — absent while the
     * primary plan is resolved implicitly, which is the normal state of a floor
     * nobody has clicked "Use as underlay" on.
     */
    const primaryRegistrationUid = docState?.doc.primaryRegistrationUid ?? null

    /**
     * Materializes that registration, so `C` and `M` work on any floor
     * (spec 2026-09-05-07 §3).
     *
     * The row places the plan exactly where the scene already had it — the
     * scene's own composed transform — so **nothing moves**: the underlay stays
     * where it was and the geometry stays glued to it. It is done here rather
     * than as a backend convenience on the calibration PATCH so the diff/undo
     * model sees the create, and it is deliberately kept when the user cancels:
     * an identity registration is a valid, harmless row, and re-materializing
     * it on the next `C` would be worse than leaving it.
     *
     * Its provenance is `materialized`, NOT the `auto_fit` "Use as underlay"
     * writes (models.FrameMethodMaterialized — keep the two in sync). Nobody
     * asked for this row: the user pressed `C`. The activity feed keys on that
     * to leave its creation out of the asset's history while still showing
     * everything done to it afterwards.
     */
    const materializePrimaryRegistration = useCallback(async (): Promise<string | null> => {
        const engine = engineRef.current
        const entries = (sceneQuery.data?.entries ?? []) as {
            primary?: boolean
            implicit?: boolean
            registration_uid?: string
            underlay_height?: number
            transform?: { tx: number; ty: number; rotation_deg: number; scale: number }
            representation?: { uid: string; kind: string; value?: unknown }
        }[]
        const entry = entries.find((e) => e.primary
            && (e.representation?.kind === 'image' || e.representation?.kind === 'pdf'))
        if (!engine || !entry?.representation || entry.registration_uid) return entry?.registration_uid ?? null

        const created = await registerUnderlay.mutateAsync({
            params: { path: { orgCode: org, assetUid } },
            body: {
                source_representation_uid: entry.representation.uid,
                target_kind: 'asset',
                // The composed transform the scene already reports, not a fresh
                // auto-fit: re-fitting an underlay that is already placed would
                // move the drawing under the user's cursor.
                transform: entry.transform ?? { tx: 0, ty: 0, rotation_deg: 0, scale: 1 },
                provenance: { method: 'materialized', confidence: 'low' },
            },
        } as never) as { uid?: string }

        const fresh = await sceneQuery.refetch()
        if (fresh.data) {
            const doc = applyGroupers(sceneFromPlanScene(fresh.data))
            baselineRef.current = cloneDoc(doc)
            engine.setDoc(doc)
            syncFromEngine()
        }
        return created?.uid ?? null
    }, [sceneQuery, org, assetUid, registerUnderlay, applyGroupers, syncFromEngine])

    // Entering calibrate mode (`C`, the toolbar, the badge shortcut) is what
    // triggers it. `M` only works inside calibrate mode, so this one hook
    // covers both of the spec's entry points.
    const materializingRef = useRef(false)
    useEffect(() => {
        if (tool !== 'calibrate' || materializingRef.current) return
        const engine = engineRef.current
        if (!engine) return
        if (primaryRegistrationUid) {
            // §3: on a floor or a unit the calibrate panel defaults to the
            // primary plan rather than making the user pick it out of a list.
            if (!calibration.registrationUid) {
                engine.setCalibrationTarget(primaryRegistrationUid)
            }
            return
        }
        materializingRef.current = true
        void materializePrimaryRegistration()
            .then((uid) => {
                if (uid) engineRef.current?.setCalibrationTarget(uid)
            })
            .catch((err) => setCalibrateError(t('planEditor.calibrate_error', {
                message: (err as { message?: string })?.message ?? String(err),
            })))
            .finally(() => { materializingRef.current = false })
    }, [tool, primaryRegistrationUid, calibration.registrationUid, materializePrimaryRegistration, t])

    /** The badge's Calibrate shortcut: enter the §3 flow in one click. */
    const onCalibrateShortcut = useCallback(() => {
        engineRef.current?.setTool('calibrate')
        setTool('calibrate')
    }, [])

    const onSelectLayer = useCallback((registrationUid: string | null) => {
        engineRef.current?.selectLayer(registrationUid)
    }, [])

    /* ---- promotion / demotion / wall re-target (spec 03 §3/§4/§5) ---- */

    /**
     * Promotes one element by key. Everything is read from the engine's live
     * document rather than from React state, because "Promote all" (spec
     * 2026-09-03-01 §F5) runs this in a loop within a single render: a
     * `selectedKey`-based implementation would promote the same element N
     * times.
     */
    const promoteByKey = useCallback((key: string) => {
        const engine = engineRef.current
        if (!engine) return
        const el = elementByKey(engine.getDoc(), key)
        // Walls and runs live in a bundle by design: a run depicts its network
        // asset by link, it never becomes an asset of its own.
        if (!el || el.kind === 'wall' || el.kind === 'run' || el.kind === 'riser') return
        engine.select(key)
        // An element that already *depicts* an asset keeps it: promoting only
        // gives it its own representation. Anything else creates its asset
        // through the same create-by-default flow drawing uses (§3).
        if (el.owner.assetUid && el.owner.assetUid !== engine.getDoc().frame.uid) {
            engine.promoteSelected({ ...el.owner })
            return
        }
        const point = el.kind === 'space'
            ? centroid(el.boundary)
            : el.kind === 'opening' ? el.center : el.position
        const hint = el.kind === 'opening'
            ? {
                openingKind: el.openingKind,
                wallOwnerUid: el.wallKey
                    ? repAssetUidOf(elementByKey(engine.getDoc(), el.wallKey) ?? el)
                    : el.wallRef?.assetUid,
            }
            : el.kind === 'equipment' ? { typeCode: el.typeCode }
                : el.kind === 'space' && el.typeHint ? { typeCode: el.typeHint }
                    : undefined
        const owner = ownerForCreated(el.kind, point, hint)
        if (owner.newAsset && el.kind === 'space' && el.name) owner.newAsset.name = el.name
        engine.promoteSelected(owner)
    }, [ownerForCreated])

    const onPromote = useCallback(() => {
        if (!selectedKey) return
        promoteByKey(selectedKey)
        syncFromEngine()
    }, [selectedKey, promoteByKey, syncFromEngine])

    /**
     * The split warning's one-click promote (§4): select the offending element,
     * then run the ordinary promote flow on it.
     */
    const onPromoteIssue = useCallback((key: string) => {
        engineRef.current?.select(key)
        engineRef.current?.panTo(key)
        window.setTimeout(() => onPromoteRef.current(), 0)
    }, [])

    const onPromoteRef = useRef(onPromote)
    useEffect(() => {
        onPromoteRef.current = onPromote
    }, [onPromote])

    // Computed from the document, never from the engine ref: this runs during
    // render, and the ref is not a rendering input.
    const demoteTargets = useMemo(() => {
        const el = docState && selectedKey ? elementByKey(docState.doc, selectedKey) : null
        if (!docState || !el) return []
        return bundleTargets(docState.doc, el).map((target) => ({
            repUid: target.repUid,
            label: target.label,
            elementCount: target.elementCount,
        }))
    }, [docState, selectedKey])

    const onDemote = useCallback((repUid: string) => {
        const engine = engineRef.current
        const target = engine?.demoteTargets().find((b) => b.repUid === repUid)
        if (!engine || !target) return
        engine.demoteSelected(target)
        syncFromEngine()
    }, [syncFromEngine])

    const onRetargetWall = useCallback((ownerUid: string) => {
        const engine = engineRef.current
        const target = ctxRef.current.assetIndex.get(ownerUid)
        if (!engine) return
        engine.retargetSelectedWall({
            assetUid: ownerUid,
            assetName: target?.name,
            assetTypeCode: target?.typeCode,
            assetParentUid: target?.parentUid,
            // A unit anchors its own frame: a wall re-owned by it is drawn on
            // this layer but resolved from the unit's, so its representation
            // needs the explicit row that says which layer (§1).
            ...(needsExplicitRegistration(ownerUid, ctxRef.current.assetIndex, assetUid)
                ? { crossLayer: true }
                : {}),
        })
        syncFromEngine()
    }, [syncFromEngine, assetUid])

    const promotable = useMemo(() => {
        const el = docState && selectedKey ? elementByKey(docState.doc, selectedKey) : null
        return !!docState && !!el && canPromote(docState.doc, el)
    }, [docState, selectedKey])

    /* ---- floorplan-v1 import (spec 03 §7) ---- */

    const onImportFile = useCallback(async (file: File) => {
        const engine = engineRef.current
        if (!engine) return
        setImportNotice(null)
        if (engine.isDirty()) {
            setImportNotice({ error: true, message: t('planEditor.import_dirty') })
            return
        }
        let raw: unknown
        try {
            raw = JSON.parse(await file.text())
        } catch {
            setImportNotice({ error: true, message: t('planEditor.import_errors.not_json') })
            return
        }
        const parsed = parseFloorplanV1(raw)
        if (!parsed.ok) {
            setImportNotice({
                error: true,
                message: parsed.errors.map((e) => t(`planEditor.import_errors.${e.code}`, e.params)).join(' · '),
            })
            return
        }
        setImporting(true)
        try {
            // One all-in-one representation on the context asset, auto-fitted
            // over whatever the frame already shows; promotion splits it (§3).
            const transform = autoFitTransform(
                bboxOfPoints(importedPoints(parsed.result.value)),
                bboxOfPoints(documentPoints(engine.getDoc())),
            )
            await createImportedRep.mutateAsync({
                params: { path: { orgCode: org, assetUid } },
                body: {
                    kind: 'vector',
                    value: parsed.result.value,
                    frame: { target_asset_uid: assetUid, transform },
                },
            } as never)
            const fresh = await sceneQuery.refetch()
            if (fresh.data) {
                const doc = applyGroupers(sceneFromPlanScene(fresh.data))
                baselineRef.current = cloneDoc(doc)
                engine.setDoc(doc)
                engine.fitView()
            }
            const warnings = parsed.result.warnings
                .map((w) => t(`planEditor.import_errors.${w.code}`, w.params))
                .join(' · ')
            setImportNotice({
                error: false,
                message: `${t('planEditor.import_done', parsed.result.counts)}${warnings ? ` — ${warnings}` : ''}`,
            })
        } catch (err) {
            setImportNotice({
                error: true,
                message: t('planEditor.import_failed', { message: (err as { message?: string })?.message ?? String(err) }),
            })
        } finally {
            setImporting(false)
            syncFromEngine()
        }
    }, [org, assetUid, createImportedRep, sceneQuery, syncFromEngine, t, applyGroupers])

    /* ---- digitization (spec 2026-09-03-01 §F1–§F3) ---- */

    /**
     * The frame's drawing surface: the scan a digitization reads. Same entry
     * the backend defaults to, so the dialog names what the job will use.
     */
    const primaryScan = useMemo(() => {
        const entries = (sceneQuery.data?.entries ?? []) as {
            primary?: boolean
            representation?: { uid: string; kind: string; value?: unknown }
        }[]
        const entry = entries.find((e) => e.primary && (e.representation?.kind === 'image' || e.representation?.kind === 'pdf'))
        if (!entry?.representation) return null
        const value = entry.representation.value as { title?: string; sheets?: { page?: number }[] } | undefined
        return {
            repUid: entry.representation.uid,
            title: value?.title?.trim() || t('planEditor.untitled_plan'),
            page: value?.sheets?.[0]?.page ?? 1,
        }
    }, [sceneQuery.data, t])

    /** A live bundle already extracted from that same scan (spec decision 8). */
    const hasPreviousExtraction = useMemo(() => {
        const provenance = docState?.doc.repProvenance
        if (!provenance || !primaryScan) return false
        return Object.values(provenance).some((p) => p?.source_representation_uid === primaryScan.repUid)
    }, [docState, primaryScan])

    const onDigitizeFinished = useCallback(async (output: DigitizeOutput, jobUid: string) => {
        // §5 of spec 2026-09-30-04: errors mean nothing was written — show the
        // draft and let the user decide, instead of loading a scene that did
        // not change.
        if (output.preview && !output.preview.accepted_at && !output.representation_uid) {
            setDigitizePreviewError(null)
            setDigitizePreview({ jobUid, output })
            return
        }
        const engine = engineRef.current
        // Both: the scene carries the geometry, the representations list carries
        // the creation date the provenance line prints.
        const [fresh] = await Promise.all([sceneQuery.refetch(), repsQuery.refetch()])
        let doors = 0
        let windows = 0
        let spaces = output.counts?.spaces ?? 0
        if (engine && fresh.data) {
            const doc = applyGroupers(sceneFromPlanScene(fresh.data))
            baselineRef.current = cloneDoc(doc)
            engine.setDoc(doc)
            engine.fitView()
            const bundle = doc.elements.filter((el) => el.source.repUid === output.representation_uid)
            for (const el of bundle) {
                if (el.kind !== 'opening') continue
                if (el.openingKind === 'window') windows++
                else doors++
            }
            const bundleSpaces = bundle.filter((el) => el.kind === 'space')
            if (bundleSpaces.length) spaces = bundleSpaces.length
            // Land on something reviewable — a room, not the first wall in
            // document order: walls are not promotable, so selecting one would
            // hide both Promote and Promote all on the very bundle that needs
            // them.
            const landing = bundle.find((el) => el.kind === 'space') ?? bundle[0]
            if (landing) engine.select(landing.key)
        }
        const matched = output.matches?.length ?? 0
        const ambiguous = output.ambiguous_labels?.length ?? 0
        const summaryText = t('planEditor.digitize.summary', {
            spaces,
            walls: output.counts?.walls ?? 0,
            doors,
            windows,
            equipments: output.counts?.equipments ?? 0,
            matched,
            unmatched: Math.max(0, spaces - matched),
        })
        // §5a: the summary counts what validation found, so a run that produced
        // geometry nobody should promote says so on the strip rather than only
        // in the panel below.
        const issueCount = output.validation?.issues?.length ?? 0
        const parts = [summaryText]
        if (ambiguous > 0) parts.push(t('planEditor.digitize.ambiguous', { count: ambiguous }))
        if (issueCount > 0) parts.push(t('planEditor.digitize.issues', { count: issueCount }))
        digitizeRef.current?.setNotice({ error: false, message: parts.join(' ') })
        syncFromEngine()
    }, [sceneQuery, repsQuery, applyGroupers, t, syncFromEngine])

    const digitize = useDigitizePlan(org, assetUid, onDigitizeFinished)
    // Beta flag: hidden while loading so the buttons don't flash (spec 2026-09-30-03).
    const aiDigitizeEnabled = useFeatureFlag<boolean>(org, 'plan-editor.ai-digitize', false)
    const aiDigitizeAvailable = digitize.available && aiDigitizeEnabled === true
    // The hook is created after the callback that reports into it, so the
    // callback reaches it through a ref rather than a forward reference.
    const digitizeRef = useRef(digitize)
    useEffect(() => {
        digitizeRef.current = digitize
    }, [digitize])

    /**
     * §5a: the bundles a digitization produced. Only their geometry is
     * validated — a floor whose walls somebody traced by hand is not an
     * extraction and must not be judged as one.
     */
    const digitizedRepUids = useMemo(() => {
        const provenance = docState?.doc.repProvenance
        if (!provenance) return null
        const uids = new Set(
            Object.entries(provenance)
                .filter(([, p]) => p?.method === 'auto_vision')
                .map(([uid]) => uid),
        )
        return uids.size ? uids : null
    }, [docState])

    /**
     * The client-side run of the same eleven checks the job stored (§5a). It is
     * what makes a badge clear the moment the user fixes the element, instead
     * of after another half-minute run.
     */
    const floorplanIssues = useMemo<FloorplanIssue[]>(() => {
        if (!docState || !digitizedRepUids) return []
        return validateFloorplan(docState.doc, frameMmPerUnit, digitizedRepUids)
    }, [docState, frameMmPerUnit, digitizedRepUids])

    const floorplanIssuesByKey = useMemo(
        () => floorplanIssuesByElement(floorplanIssues),
        [floorplanIssues],
    )
    const floorplanBlocked = useMemo(() => hasFloorplanErrors(floorplanIssues), [floorplanIssues])

    /**
     * One list for the panel: the editor's own live checks plus §5a's. They are
     * the same shape by construction — the floorplan codes translate through
     * `planEditor.issues.<code>` like every other one — so the panel needs no
     * second rendering path, and a run's "N issues" is simply this list's
     * length.
     */
    const allIssues = useMemo<ValidationIssue[]>(
        () => [...floorplanIssues as ValidationIssue[], ...issues],
        [floorplanIssues, issues],
    )

    const digitizeDisabledReason = useMemo(() => {
        if (!primaryScan) return t('planEditor.digitize.no_scan')
        // §2: an uncalibrated frame produces a bundle whose rooms read `u²` and
        // whose geometry nothing can judge. Calibration is a precondition, not
        // a nicety — and there is deliberately no way to force past it.
        if (frameMmPerUnit === null) return t('planEditor.digitize.not_calibrated')
        if (digitize.running || digitize.enqueueing) return t('planEditor.digitize.running')
        if (dirty) return t('planEditor.digitize.dirty')
        return null
    }, [primaryScan, frameMmPerUnit, digitize.running, digitize.enqueueing, dirty, t])

    /**
     * Spec 2026-09-30-04 §1: an automatic scale the calibrate job did not
     * vouch for. The server refuses it (`CALIBRATION_LOW_CONFIDENCE`); the
     * dialog says so first and offers the same two ways out as "not to scale".
     */
    const calibrationUntrusted = useMemo(
        () => frameMmPerUnit !== null && isUntrustedCalibration(docState?.doc.calibration),
        [frameMmPerUnit, docState],
    )

    /* ---- auto-calibration (spec 2026-09-05-07 §4) ---- */

    const reloadAfterCalibration = useCallback(async (output: CalibrateOutput) => {
        const engine = engineRef.current
        const fresh = await sceneQuery.refetch()
        if (engine && fresh.data) {
            const doc = applyGroupers(sceneFromPlanScene(fresh.data))
            baselineRef.current = cloneDoc(doc)
            engine.setDoc(doc)
            syncFromEngine()
        }
        const consensus = output.consensus
        const measure = consensus?.measure
        // §4: draw the segment the scale came from, so "2.51 m ↔ 312 px" is
        // answerable by looking at the plan. The job hands the endpoints back
        // in the frame's own units, exactly like every other calibration
        // measure, so no conversion happens here.
        const stored = output.measure_frame
        if (engine && stored) engine.flashMeasure(stored.a, stored.b)
        // §5 of spec 2026-09-14-01: when the dimension lines gave nothing and
        // the Ø 1.50 m turning circle did, the summary has to say so — "4
        // dimensions" would be a lie about a plan that carries none. The
        // agreement clause only exists once two circles backed each other up.
        const circles = consensus?.survivors?.length ?? 0
        const message = consensus?.basis === 'pmr_circle'
            ? t('planEditor.autoCalibrate.summary_pmr_circle', {
                pixels: measure ? Math.round(measure.length_px) : 0,
                agreement: circles > 1
                    ? t('planEditor.autoCalibrate.pmr_circle_agreement', {
                        count: circles,
                        spread: (consensus?.spread_pct ?? 0).toFixed(1),
                    })
                    : '',
            })
            : t('planEditor.autoCalibrate.summary', {
                count: circles,
                distance: measure ? (measure.value_mm / 1000).toFixed(2) : '—',
                pixels: measure ? Math.round(measure.length_px) : 0,
                spread: (consensus?.spread_pct ?? 0).toFixed(1),
            })
        autoCalibrateRef.current?.setNotice({
            error: false,
            measure,
            message: message + (consensus?.rejected_cluster
                ? ' ' + t('planEditor.autoCalibrate.second_scale', {
                    count: consensus.rejected_cluster.count,
                    ratio: consensus.rejected_cluster.ratio_to_chosen.toFixed(2),
                })
                : ''),
        })
    }, [sceneQuery, applyGroupers, syncFromEngine, t])

    const onCalibrateFinished = useCallback(async (output: CalibrateOutput, jobUid: string) => {
        if (output.confidence === 'low' && output.proposed_calibration) {
            // Nothing was written: show the circle the job measured and ask.
            setProposalError(null)
            setCalibrationProposal({ jobUid, output })
            // A printed-scale proposal has no turning circle to show: it
            // flashes the reading that agrees with it, if any, and nothing for
            // its synthetic segment.
            const stored = output.measure_frame
            if (stored && output.consensus?.basis !== 'printed_scale') {
                engineRef.current?.showCalibrationProposal(stored.a, stored.b)
            } else if (stored && output.consensus?.measure?.kind !== 'printed_scale') {
                engineRef.current?.flashMeasure(stored.a, stored.b)
            }
            return
        }
        await reloadAfterCalibration(output)
    }, [reloadAfterCalibration])

    const acceptCalibration = $api.useMutation('post', '/api/v1/organizations/{orgCode}/assets/{assetUid}/plan/calibrate/accept')

    const dropCalibrationProposal = useCallback(() => {
        engineRef.current?.clearCalibrationProposal()
        setCalibrationProposal(null)
        setProposalError(null)
    }, [])

    const onAcceptCalibrationProposal = useCallback(async () => {
        if (!calibrationProposal) return
        setProposalError(null)
        try {
            await acceptCalibration.mutateAsync({
                params: { path: { orgCode: org, assetUid } },
                body: { job_uid: calibrationProposal.jobUid },
            })
        } catch (err) {
            setProposalError(apiErrorMessage(err))
            return
        }
        const { output } = calibrationProposal
        dropCalibrationProposal()
        await reloadAfterCalibration(output)
    }, [calibrationProposal, acceptCalibration, org, assetUid, dropCalibrationProposal, reloadAfterCalibration])

    const onProposalMeasureByHand = useCallback(() => {
        dropCalibrationProposal()
        onCalibrateShortcut()
    }, [dropCalibrationProposal, onCalibrateShortcut])

    const proposalMessage = useMemo(() => {
        const consensus = calibrationProposal?.output.consensus
        if (!consensus) return ''
        // Spec 2026-10-16-03: the scale printed on the sheet answered, either
        // because the drawing gave none or because it contradicted it.
        const printed = consensus.printed_scale
        if (consensus.basis === 'printed_scale' && printed) {
            return t('planEditor.autoCalibrate.proposal_printed_scale', {
                reason: printed.verdict === 'replaced'
                    ? t('planEditor.autoCalibrate.proposal_printed_reason_replaced', {
                        readings: (printed.readings_mm_per_px ?? 0).toFixed(1),
                    })
                    : t('planEditor.autoCalibrate.proposal_printed_reason_answered'),
                denominator: printed.denominator ?? 0,
                mmPerPx: (consensus.mm_per_px ?? 0).toFixed(2),
                dpi: Math.round(printed.dpi ?? 0),
            })
        }
        // Spec 2026-10-11-06 §5: three circles measured in the pixels (boxed
        // or found by the sheet search) agreeing within 3 % are what the job
        // writes without asking; the reason names what was missing.
        const measured = (consensus.survivors ?? [])
            .filter((c) => c.source === 'measured' || c.source === 'searched').length
        const agreeing = measured >= 3 && (consensus.spread_pct ?? 100) <= 3
        const reasonKey = printed?.verdict === 'disagrees'
            ? 'planEditor.autoCalibrate.proposal_reason_printed_scale'
            : agreeing
            ? 'planEditor.autoCalibrate.proposal_reason_dimensions'
            : measured >= 2
                ? 'planEditor.autoCalibrate.proposal_reason_few_circles'
                : measured === 1
                    ? 'planEditor.autoCalibrate.proposal_reason_one_circle'
                    : 'planEditor.autoCalibrate.proposal_reason_model'
        return t('planEditor.autoCalibrate.proposal', {
            reason: t(reasonKey),
            pixels: consensus.measure ? Math.round(consensus.measure.length_px) : 0,
            mmPerPx: (consensus.mm_per_px ?? 0).toFixed(1),
        })
    }, [calibrationProposal, t])

    const autoCalibrate = useCalibratePlan(org, assetUid, aiDigitizeAvailable, onCalibrateFinished)
    const autoCalibrateRef = useRef(autoCalibrate)
    useEffect(() => {
        autoCalibrateRef.current = autoCalibrate
    }, [autoCalibrate])

    const autoCalibrateDisabledReason = useMemo(() => {
        if (!primaryScan) return t('planEditor.digitize.no_scan')
        if (autoCalibrate.running || autoCalibrate.enqueueing) return t('planEditor.autoCalibrate.running')
        if (digitize.running) return t('planEditor.digitize.running')
        if (dirty) return t('planEditor.digitize.dirty')
        return null
    }, [primaryScan, autoCalibrate.running, autoCalibrate.enqueueing, digitize.running, dirty, t])

    const onAutoCalibrateConfirm = useCallback(async () => {
        dropCalibrationProposal()
        setAutoCalibrateOpen(false)
        setDigitizeOpen(false)
        await autoCalibrate.start({ representationUid: primaryScan?.repUid })
    }, [autoCalibrate, primaryScan, dropCalibrationProposal])

    /**
     * §2: the endpoint refused because the frame is not calibrated, which means
     * the editor was looking at a stale scene — reload it, so the retry opens
     * the dialog on the state the server actually sees.
     */
    const staleCalibration = digitize.notice?.staleCalibration ?? false
    useEffect(() => {
        if (!staleCalibration) return
        void (async () => {
            const engine = engineRef.current
            const fresh = await sceneQuery.refetch()
            if (!engine || !fresh.data) return
            const doc = applyGroupers(sceneFromPlanScene(fresh.data))
            baselineRef.current = cloneDoc(doc)
            engine.setDoc(doc)
            syncFromEngine()
        })()
    }, [staleCalibration, sceneQuery, applyGroupers, syncFromEngine])

    const acceptDigitize = $api.useMutation('post', '/api/v1/organizations/{orgCode}/assets/{assetUid}/plan/digitize/accept')
    const onDigitizePreviewAccept = useCallback(async (keep: DigitizeKeep) => {
        if (!digitizePreview) return
        setDigitizePreviewError(null)
        try {
            const res = await acceptDigitize.mutateAsync({
                params: { path: { orgCode: org, assetUid } },
                body: { job_uid: digitizePreview.jobUid, keep },
            })
            const { output, jobUid } = digitizePreview
            setDigitizePreview(null)
            await onDigitizeFinished({
                ...output,
                preview: undefined,
                representation_uid: res.representation_uid,
                counts: { ...output.counts, spaces: res.spaces },
            }, jobUid)
        } catch (err) {
            setDigitizePreviewError(apiErrorMessage(err))
        }
    }, [acceptDigitize, digitizePreview, onDigitizeFinished, org, assetUid])
    /* ---- plan proposals and layer disciplines (spec 2026-10-21-11) ---- */

    /** Files an underlay under a discipline, in place: unsaved edits stay. */
    const onUnderlayDiscipline = useCallback(async (repUid: string, discipline: string | null) => {
        await patchRepresentation.mutateAsync({
            params: { path: { orgCode: org, repUid } },
            body: { discipline } as never,
        })
        for (const doc of [engineRef.current?.getDoc(), baselineRef.current]) {
            for (const u of doc?.underlays ?? []) {
                if (u.repUid === repUid) u.discipline = discipline
            }
        }
        syncFromEngine()
    }, [patchRepresentation, org, syncFromEngine])

    const onProposalDecided = useCallback(async (accepted: boolean) => {
        const [fresh, list] = await Promise.all([
            accepted ? sceneQuery.refetch() : Promise.resolve(null),
            planProposals.refetch(),
            accepted ? repsQuery.refetch() : Promise.resolve(null),
            accepted ? registrationsQuery.refetch() : Promise.resolve(null),
        ])
        const remaining = list.data?.data?.length ?? 0
        if (remaining === 0) setProposalsOpen(false)
        const engine = engineRef.current
        if (fresh?.data && engine) {
            const doc = applyGroupers(sceneFromPlanScene(fresh.data))
            baselineRef.current = cloneDoc(doc)
            engine.setDoc(doc)
            syncFromEngine()
        }
    }, [sceneQuery, planProposals, repsQuery, registrationsQuery, applyGroupers, syncFromEngine])

    const underlayLabelOf = useCallback((repUid: string) =>
        docState?.doc.underlays.find((u) => u.repUid === repUid)?.assetName, [docState])

    const onDigitizePreviewDiscard = useCallback(() => {
        setDigitizePreview(null)
        setDigitizePreviewError(null)
        digitizeRef.current?.setNotice({ error: false, message: t('planEditor.digitizePreview.discarded') })
    }, [t])

    const onDigitizeConfirm = useCallback(async () => {
        setDigitizeOpen(false)
        await digitize.start({
            representationUid: primaryScan?.repUid,
            replace: digitizeReplace,
        })
    }, [digitize, primaryScan, digitizeReplace])

    /* ---- promote all (spec 2026-09-03-01 §F5) ---- */

    /**
     * What "Promote all" would write on the selected element's bundle: every
     * space and every opening. Walls stay — a floor's structure legitimately
     * lives in one representation — and so does equipment, whose kind does not
     * map onto an asset type unambiguously.
     */
    const promoteAllPlan = useMemo(() => {
        const doc = docState?.doc
        const el = doc && selectedKey ? elementByKey(doc, selectedKey) : null
        if (!doc || !el || !el.source.repUid) return null
        const members = doc.elements.filter((e) => e.source.repUid === el.source.repUid)
        const promotable = members.filter((e) => canPromote(doc, e) && (e.kind === 'space' || e.kind === 'opening'))
        if (!promotable.length) return null
        const spaces = promotable.filter((e) => e.kind === 'space')
        return {
            keys: promotable.map((e) => e.key),
            spaces: spaces.length,
            openings: promotable.length - spaces.length,
            matched: spaces.filter((e) => e.owner.assetUid && e.owner.assetUid !== doc.frame.uid).length,
            equipments: members.filter((e) => e.kind === 'equipment').length,
            walls: members.filter((e) => e.kind === 'wall').length,
        }
    }, [docState, selectedKey])

    /**
     * The provenance line the inspector shows on a digitized element (§F4):
     * where this geometry came from, and with what.
     */
    const selectedProvenance = useMemo(() => {
        const doc = docState?.doc
        const el = doc && selectedKey ? elementByKey(doc, selectedKey) : null
        const repUid = el?.source.repUid
        if (!doc || !repUid) return null
        const p = doc.repProvenance?.[repUid]
        if (!p) return null
        const source = doc.repTitles?.[repUid] || t('planEditor.untitled_plan')
        // *When* it was digitized is the representation's own creation date —
        // the scene payload carries no timestamp, the representations list
        // does, and the bundle is owned by this very asset so it is always in
        // that list.
        const reps = (repsQuery.data?.representations ?? []) as { uid: string; created_at?: string }[]
        const createdAt = reps.find((r) => r.uid === repUid)?.created_at
        const date = createdAt ? formatDate(createdAt, { dateStyle: 'medium' }) : ''
        if (p.model) {
            return date
                ? t('planEditor.digitize.provenance', { source, model: p.model, date })
                : t('planEditor.digitize.provenance_no_date', { source, model: p.model })
        }
        return date
            ? t('planEditor.digitize.provenance_unknown_model', { source, date })
            : t('planEditor.digitize.provenance_unknown_model_no_date', { source })
    }, [docState, selectedKey, repsQuery.data, formatDate, t])

    const onPromoteAll = useCallback(() => {
        const engine = engineRef.current
        const plan = promoteAllPlan
        if (!engine || !plan) return
        setPromoteAllOpen(false)
        // One element at a time through the ordinary promotion, so partial
        // failure and Retry behave exactly as they do today.
        for (const key of plan.keys) promoteByKey(key)
        engine.select(plan.keys[0])
        syncFromEngine()
    }, [promoteAllPlan, promoteByKey, syncFromEngine])

    /* ---- underlays not yet registered (§10) ---- */

    const unregisteredUnderlays = useMemo(() => {
        const registered = new Set((docState?.doc.underlays ?? []).map((u) => u.repUid))
        const reps = (repsQuery.data?.representations ?? []) as { uid: string; kind: string; value?: unknown }[]
        return reps
            .filter((rep) => (rep.kind === 'image' || rep.kind === 'pdf') && !registered.has(rep.uid))
            .map((rep) => ({
                repUid: rep.uid,
                title: (rep.value as { title?: string } | undefined)?.title || t('planEditor.untitled_plan'),
                aspect: rasterAspect(rep.value),
            }))
    }, [repsQuery.data, docState, t])

    const onUseAsUnderlay = useCallback(async (repUid: string) => {
        const engine = engineRef.current
        if (!engine) return
        const entry = unregisteredUnderlays.find((u) => u.repUid === repUid)
        const bb = bboxOfPoints(geometryPoints(engine.getDoc()))
        const transform = autoFitUnderlayTransform(bb, entry?.aspect ?? 1)
        await registerUnderlay.mutateAsync({
            params: { path: { orgCode: org, assetUid } },
            body: {
                source_representation_uid: repUid,
                target_kind: 'asset',
                transform,
                provenance: { method: 'auto_fit', confidence: 'low' },
            },
        } as never)
        const fresh = await sceneQuery.refetch()
        if (fresh.data) {
            const doc = applyGroupers(sceneFromPlanScene(fresh.data))
            baselineRef.current = cloneDoc(doc)
            engine.setDoc(doc)
            engine.fitView()
            syncFromEngine()
        }
    }, [unregisteredUnderlays, org, assetUid, registerUnderlay, sceneQuery, syncFromEngine, applyGroupers])

    /* ---- save (§4) ---- */

    const onSave = useCallback(async () => {
        const engine = engineRef.current
        const baseline = baselineRef.current
        if (!engine || !baseline) return
        const doc = engine.getDoc()

        // Every run depicts a network asset (spec 2026-10-20-33): the ones not
        // linked get their level parent's, reused or pending — one per
        // (parent, network), never one per segment.
        assignRunNetworkAssets(doc, networkCtxRef.current)
        // The floor segments this save creates and the building network each
        // joins, decided before the pending assets resolve.
        const attachments = segmentsToAttach(doc, networkCtxRef.current)

        // Everything drawn but never named takes its type as a name — except a
        // **room**, which is sent with no name so the *server* names it from
        // its type and its number and keeps that name current under the
        // `auto_name` flag (spec 2026-08-31-02 §2). Naming it here is what
        // used to fill the inventory with assets called "Chambre" that stopped
        // being true the moment the type changed.
        for (const el of doc.elements) {
            const pending = el.owner.newAsset
            if (!pending || pending.name) continue
            if (roomTypeCodes.has(pending.typeCode)) {
                // A drawn label — an imported one, or a name typed on the
                // element rather than on the asset — is still a name.
                if (el.kind === 'space' && el.name) pending.name = el.name
                continue
            }
            pending.name = el.kind === 'space' && el.name
                ? el.name
                : ctxRef.current.localizedTypeLabel(pending.typeCode)
        }

        const diff = computeDiff(baseline, doc, {
            drawnElsewhere,
            grouperMeta: grouperMetaRef.current,
            typeLabelOf: ctxRef.current.localizedTypeLabel,
        })
        // Refuse the save rather than post something the API will reject. The
        // validation panel already flags each element (`parent_ontology_refused`);
        // this is the half that makes "blocking" true.
        if (diff.refusedAssets.length) {
            setRefusedParents(diff.refusedAssets.map((a) => ({ name: a.name, typeCode: a.typeCode })))

            return
        }
        setRefusedParents([])

        if (isEmptyDiff(diff)) {
            engine.markSaved()
            return
        }

        // Group membership must be captured before the pending assets resolve:
        // an element's group id changes the moment its asset gets a uid.
        const groupMembers = new Map<string, string[]>(
            groupByRepresentation(doc).map((g) => [g.id, g.elements.map((e) => e.key)]),
        )

        const outcome = await save(diff, (code) => assetTypeByCode.get(code)?.uid)

        // Grouping trouble goes to the field, not only to the summary strip.
        const byGrouperKey: Record<string, string> = {}
        const bySpace: Record<string, string> = {}
        /** Saved groupers whose *new* code is already taken elsewhere. */
        const codeConflicts: string[] = []
        for (const failure of outcome.failures) {
            if (failure.kind === 'asset' && failure.assetKey && failure.code === 'DUPLICATE_GROUPER_NAME') {
                byGrouperKey[failure.assetKey] = t('planEditor.grouping_duplicate_name', { name: failure.label })
            }
            // A code is an external id, and those are unique across the whole
            // organization — not just this building group (spec 2026-08-30-03
            // §3). "External ID conflict" is the API's words for it; the field
            // the code was typed into deserves better.
            if (failure.code === 'EXTERNAL_ID_CONFLICT') codeConflicts.push(failure.label)
            if (failure.kind === 'relation' && failure.spaceKey) {
                bySpace[failure.spaceKey] = failure.code === 'relation_cardinality_violated'
                    ? t('planEditor.grouping_cardinality_conflict')
                    : t('planEditor.grouping_failed', { message: failure.message })
            }
        }
        setCodeError(codeConflicts.length ? t('planEditor.grouping_duplicate_code') : null)
        setGrouperErrors({ byGrouperKey, bySpace })

        // Fold the results back into the live document so a retry only retries
        // what actually failed.
        for (const el of doc.elements) {
            const pending = el.owner.newAsset
            if (!pending) continue
            const uid = outcome.createdAssets[pending.key]
            if (!uid) continue
            el.owner = {
                assetUid: uid,
                assetName: pending.name,
                assetTypeCode: pending.typeCode,
                assetParentUid: pending.parentUid,
            }
        }
        // Best effort: a segment that could not join stays alone, and the
        // inspector offers Part of.
        const attached = await Promise.all(attachments.map(async ({ key, assemblyUid }) => {
            const segmentUid = outcome.createdAssets[key]
            if (!segmentUid) return false
            try {
                await request('/api/v1/organizations/{orgCode}/assets/{assetUid}/network-assembly', 'PUT', {
                    params: { path: { orgCode: org, assetUid: segmentUid } },
                    body: { assembly_uid: assemblyUid },
                })
                return true
            } catch {
                return false
            }
        }))
        if (attached.some(Boolean)) void queryClient.invalidateQueries({ queryKey: ['get', NETWORK_SEGMENTS_PATH] })
        for (const [groupId, created] of Object.entries(outcome.createdReps)) {
            for (const key of groupMembers.get(groupId) ?? []) {
                const el = elementByKey(doc, key)
                if (el) el.source = { ...el.source, repUid: created.repUid, registrationUid: created.registrationUid }
            }
        }

        if (outcome.calibrationWritten) clearCalibrationEdit(doc)

        // The summary is what tells the user (and a test) the save is over, so
        // it shows once the reloaded scene is in: `setDoc` clears the
        // selection, and a room clicked between the two lost it.
        try {
            if (outcome.failures.length === 0) {
                // The memberships are refetched with the scene: the edges this
                // save just wrote are what the reloaded baseline has to carry, or
                // the very next save would try to write them again.
                const [fresh, freshRelations] = await Promise.all([sceneQuery.refetch(), relationsQuery.refetch()])
                if (fresh.data) {
                    const reloaded = applyGroupers(sceneFromPlanScene(fresh.data), freshRelations.data)
                    baselineRef.current = cloneDoc(reloaded)
                    // `setDoc` and **not** `fitView`: saving does not move anything,
                    // so the drawing must stay exactly where the user left it.
                    // Re-framing here threw away the zoom someone had just spent a
                    // minute setting up, on every save.
                    engine.setDoc(reloaded)
                } else {
                    baselineRef.current = cloneDoc(doc)
                    engine.markSaved()
                }
            } else {
                // Keep the failed representations pending by restoring their
                // pre-save state into the baseline.
                const failedRepUids = new Set(outcome.failures.map((f) => f.repUid).filter(Boolean) as string[])
                const nextBaseline = cloneDoc(doc)
                nextBaseline.elements = nextBaseline.elements.filter(
                    (el) => !el.source.repUid || !failedRepUids.has(el.source.repUid),
                )
                nextBaseline.elements.push(
                    ...baseline.elements.filter((el) => el.source.repUid && failedRepUids.has(el.source.repUid)),
                )
                // A run whose network asset was not created was saved without
                // its link: the retry has to write it, so the baseline must
                // not already carry it.
                for (const el of nextBaseline.elements) {
                    if (el.kind === 'run' && el.owner.newAsset) el.owner = { ...(el.source.repOwner ?? {}) }
                }
                baselineRef.current = nextBaseline
            }
        } finally {
            setSummary(outcome)
        }
        syncFromEngine()
    }, [save, assetTypeByCode, roomTypeCodes, sceneQuery, syncFromEngine, drawnElsewhere, applyGroupers, relationsQuery, t, request, org, queryClient])

    // Keep the keyboard's Save pointing at the current one.
    useEffect(() => {
        onSaveRef.current = () => { void onSave() }
    }, [onSave])

    /* ---- drawing targets (§7) ---- */

    const drawParents: DrawParent[] = useMemo(() => {
        const targets = new Map<string, DrawParent>()
        const self = assetIndex.get(assetUid)
        targets.set(assetUid, {
            uid: assetUid,
            name: self?.name ?? asset?.name ?? t('planEditor.this_asset'),
            typeCode: self?.typeCode,
        })
        for (const a of assetIndex.values()) {
            if (a.parentUid !== assetUid) continue
            // A room sub-type is a drawing target exactly like the plain `room`
            // it refines (spec 2026-08-31-01 §2).
            if (a.typeCode
                && ![UNIT_TYPE, COMMON_AREA_TYPE, ROOM_TYPE].includes(lotBaseTypeCode(a.typeCode))
                && !a.typeCode.startsWith(`${ROOM_TYPE}_`)) continue
            targets.set(a.uid, { uid: a.uid, name: a.name ?? a.uid, typeCode: a.typeCode })
        }
        return [...targets.values()]
    }, [assetIndex, assetUid, asset?.name, t])

    /* ---- render ---- */

    if (isMobile) {
        return (
            <div
                className="flex flex-col items-center gap-3 rounded-lg border border-border bg-card p-8 text-center"
                data-testid="plan-editor-mobile-notice"
            >
                <AppIcon tone="muted" path={mdiFloorPlan} size="xl" />
                <p className="text-sm text-muted-foreground">{t('planEditor.mobile_notice')}</p>
                <Button
                    variant="outline"
                    size="sm"
                    data-testid="plan-editor-mobile-back"
                    onClick={() => navigate({ to: '/$org/assets/$assetUid', params: { org, assetUid } })}
                >
                    {t('planEditor.back_to_asset')}
                </Button>
            </div>
        )
    }

    const assetTypeCode = asset?.asset_type_uid ? assetTypeByUid.get(asset.asset_type_uid)?.code : undefined
    if (assetTypeCode && !EDITABLE_TYPE_CODES.includes(lotBaseTypeCode(assetTypeCode))) {
        return (
            <div
                className="flex flex-col items-center gap-3 rounded-lg border border-border bg-card p-8 text-center"
                data-testid="plan-editor-unsupported"
            >
                <AppIcon tone="muted" path={mdiAlertCircleOutline} size="xl" />
                <p className="text-sm text-muted-foreground">{t('planEditor.unsupported_type')}</p>
            </div>
        )
    }

    return (
        <div className="flex h-[calc(100vh-8rem)] flex-col overflow-hidden rounded-lg border border-border bg-card" data-testid="plan-editor">
            <EditorToolbar
                tool={tool}
                onToolChange={(next) => engineRef.current?.setTool(next)}
                disabledTools={grouperAllowedHere ? undefined : { unit: t('planEditor.tool_unit_unavailable') }}
                canUndo={canUndo}
                canRedo={canRedo}
                onUndo={onUndo}
                onRedo={onRedo}
                onFit={onFit}
                onZoomIn={onZoomIn}
                onZoomOut={onZoomOut}
                snap={snap}
                onSnapChange={(on) => {
                    engineRef.current?.setSnap(on)
                    setSnap(on)
                }}
                showGroupers={showGroupers}
                onShowGroupersChange={(on) => {
                    engineRef.current?.setShowGroupers(on)
                    setShowGroupers(on)
                }}
                dirty={dirty}
                saving={saving}
                onSave={onSave}
                onImport={() => fileInputRef.current?.click()}
                importing={importing}
                onDigitize={() => {
                    // A previous extraction has to be explicitly replaced, so
                    // the box starts unticked even on a re-run.
                    setDigitizeReplace(false)
                    openDigitizeDialog()
                }}
                digitizeAvailable={aiDigitizeAvailable}
                digitizeDisabledReason={digitizeDisabledReason}
                onAutoCalibrate={() => setAutoCalibrateOpen(true)}
                autoCalibrateDisabledReason={autoCalibrateDisabledReason}
                saveBlockedReason={digitize.running ? t('planEditor.digitize.save_blocked') : null}
            />

            {/*
              * §F2: while a run is in flight the editor stays usable, but the
              * strip says what is happening and Save is off — a save that raced
              * the job's create would reload a scene the diff never saw.
              */}
            {digitize.running && primaryScan && (
                <div
                    className="flex items-center gap-2 border-b border-border bg-muted/40 px-3 py-1.5 text-xs text-muted-foreground"
                    role="status"
                    data-testid="plan-editor-digitize-progress"
                >
                    <AppIcon path={mdiLoading} size="sm" className="motion-safe:animate-spin" />
                    <span className="flex-1">
                        {t('planEditor.digitize.progress', { document: primaryScan.title, page: primaryScan.page })}
                    </span>
                    <span data-testid="plan-editor-digitize-elapsed">
                        {t('planEditor.digitize.elapsed', { seconds: digitize.elapsedSeconds })}
                    </span>
                </div>
            )}

            {/* §4: the calibration run has the same strip as the digitization. */}
            {autoCalibrate.running && primaryScan && (
                <div
                    className="flex items-center gap-2 border-b border-border bg-muted/40 px-3 py-1.5 text-xs text-muted-foreground"
                    role="status"
                    data-testid="plan-editor-autocalibrate-progress"
                >
                    <AppIcon path={mdiLoading} size="sm" className="motion-safe:animate-spin" />
                    <span className="flex-1">
                        {t('planEditor.autoCalibrate.progress', { document: primaryScan.title, page: primaryScan.page })}
                    </span>
                    <span data-testid="plan-editor-autocalibrate-elapsed">
                        {t('planEditor.digitize.elapsed', { seconds: autoCalibrate.elapsedSeconds })}
                    </span>
                </div>
            )}

            {autoCalibrate.notice && (
                <div
                    className={`flex items-center gap-2 border-b border-border px-3 py-2 text-xs ${autoCalibrate.notice.error ? 'text-destructive' : 'text-muted-foreground'}`}
                    role="status"
                    data-testid={autoCalibrate.notice.error
                        ? 'plan-editor-autocalibrate-error'
                        : 'plan-editor-autocalibrate-summary'}
                >
                    <AppIcon
                        path={autoCalibrate.notice.error ? mdiAlertCircleOutline : mdiCheckCircleOutline}
                        size="sm"
                    />
                    <span className="flex-1">
                        {autoCalibrate.notice.error
                            ? t('planEditor.autoCalibrate.failed', { message: autoCalibrate.notice.message })
                            : autoCalibrate.notice.message}
                    </span>
                    {autoCalibrate.notice.error && (
                        // §4: a failed auto-calibration always offers the way
                        // that always works.
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-[11px]"
                            data-testid="plan-editor-autocalibrate-manual"
                            onClick={() => {
                                autoCalibrate.dismiss()
                                onCalibrateShortcut()
                            }}
                        >
                            {t('planEditor.autoCalibrate.manual')}
                        </Button>
                    )}
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-[11px]"
                        data-testid="plan-editor-autocalibrate-dismiss"
                        onClick={autoCalibrate.dismiss}
                    >
                        {t('planEditor.dismiss')}
                    </Button>
                </div>
            )}

            {/*
              * Spec 2026-09-30-02 §4: the job was not sure enough to write the
              * scale. The circle is drawn on the plan; the strip says what it
              * would set and why it asks.
              */}
            {calibrationProposal && (
                <div
                    className="flex flex-wrap items-center gap-2 border-b border-border bg-warning/10 px-3 py-2 text-xs"
                    role="status"
                    data-testid="plan-editor-autocalibrate-proposal"
                >
                    <AppIcon path={mdiAlertCircleOutline} size="sm" />
                    <span className="min-w-0 flex-1">
                        {proposalMessage}
                        {proposalError && (
                            <span className="ml-1 text-destructive" data-testid="plan-editor-autocalibrate-proposal-error">
                                {t('planEditor.autoCalibrate.proposal_accept_failed', { message: proposalError })}
                            </span>
                        )}
                    </span>
                    <Button
                        variant="default"
                        size="sm"
                        className="h-6 px-2 text-[11px]"
                        data-testid="plan-editor-autocalibrate-proposal-accept"
                        disabled={acceptCalibration.isPending}
                        onClick={() => void onAcceptCalibrationProposal()}
                    >
                        {t('planEditor.autoCalibrate.proposal_accept')}
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        className="h-6 px-2 text-[11px]"
                        data-testid="plan-editor-autocalibrate-proposal-manual"
                        onClick={onProposalMeasureByHand}
                    >
                        {t('planEditor.autoCalibrate.proposal_manual')}
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-[11px]"
                        data-testid="plan-editor-autocalibrate-proposal-cancel"
                        onClick={dropCalibrationProposal}
                    >
                        {t('planEditor.autoCalibrate.proposal_cancel')}
                    </Button>
                </div>
            )}

            {/*
              * §3: a frame with no calibration is valid — people trace first
              * and measure later — but its numbers are plan units, not
              * millimetres, and the editor has to say so.
              *
              * Spec 2026-09-05-07 §3 gives the badge a way out: one click into
              * the calibrate flow, which materializes the layer it needs.
              */}
            {docState && frameMmPerUnit === null && (
                <div
                    className="flex items-center gap-2 border-b border-border bg-muted/40 px-3 py-1.5 text-xs text-muted-foreground"
                    role="status"
                >
                    <UncalibratedBadge />
                    <span className="flex-1">{t('planEditor.uncalibrated_explainer')}</span>
                    <Button
                        variant="outline"
                        size="sm"
                        className="h-6 px-2 text-[11px]"
                        data-testid="plan-editor-calibrate-shortcut"
                        onClick={onCalibrateShortcut}
                    >
                        {t('planEditor.calibrate')}
                    </Button>
                </div>
            )}

            {holeRejected && (
                <div
                    className="flex items-center gap-2 border-b border-border bg-amber-500/10 px-3 py-1.5 text-xs text-muted-foreground"
                    role="status"
                    data-testid="plan-editor-hole-rejected"
                >
                    <span className="flex-1">{t('planEditor.hole_rejected')}</span>
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-[11px]"
                        data-testid="plan-editor-hole-rejected-dismiss"
                        onClick={() => setHoleRejected(false)}
                    >
                        {t('planEditor.dismiss')}
                    </Button>
                </div>
            )}

            {footprintNotice && (
                <div
                    className="flex items-center gap-2 border-b border-border bg-amber-500/10 px-3 py-1.5 text-xs text-muted-foreground"
                    role="status"
                    data-testid="plan-editor-footprint-reduced"
                >
                    <span className="flex-1">{t('planEditor.footprint_reduced')}</span>
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-[11px]"
                        data-testid="plan-editor-footprint-reduced-dismiss"
                        onClick={() => setFootprintNotice(false)}
                    >
                        {t('planEditor.dismiss')}
                    </Button>
                </div>
            )}

            {tool === 'calibrate' && (
                <CalibratePanel
                    layers={sceneLayerList}
                    state={calibration}
                    onTargetChange={(uid) => {
                        engineRef.current?.setCalibrationTarget(uid)
                        setCalibrateError(null)
                    }}
                    onMeasure={() => engineRef.current?.startMeasure()}
                    onStatedScale={onStatedScale}
                    onShowCurrentChange={(on) => engineRef.current?.setShowCurrentCalibration(on)}
                    onClear={() => engineRef.current?.clearPairs()}
                    onApply={onCalibrateApply}
                    onCancel={onCalibrateCancel}
                    applying={calibrateApplying}
                    error={calibrateError}
                />
            )}

            {/* The import lives on a hidden input so the toolbar button and a
              * drop on the stage share one code path (spec §7). */}
            <input
                ref={fileInputRef}
                type="file"
                accept="application/json,.json"
                className="hidden"
                data-testid="plan-editor-import-input"
                onChange={(e) => {
                    const file = e.target.files?.[0]
                    e.target.value = ''
                    if (file) void onImportFile(file)
                }}
            />

            {digitize.notice && (
                <div
                    className={`flex items-center gap-2 border-b border-border px-3 py-2 text-xs ${digitize.notice.error ? 'text-destructive' : 'text-muted-foreground'}`}
                    role="status"
                    data-testid={digitize.notice.error ? 'plan-editor-digitize-error' : 'plan-editor-digitize-summary'}
                >
                    <AppIcon path={digitize.notice.error ? mdiAlertCircleOutline : mdiCheckCircleOutline} size="sm" />
                    <span className="flex-1">
                        {digitize.notice.error
                            ? t('planEditor.digitize.failed', {
                                message: digitize.notice.code === 'CALIBRATION_LOW_CONFIDENCE'
                                    ? t('planEditor.digitize.calibration_untrusted_hint')
                                    : digitize.notice.message,
                            })
                            : digitize.notice.message}
                    </span>
                    {digitize.notice.error && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-[11px]"
                            data-testid="plan-editor-digitize-retry"
                            onClick={() => {
                                setDigitizeReplace(!!digitize.notice?.retryWithReplace)
                                digitize.dismiss()
                                openDigitizeDialog()
                            }}
                        >
                            {t('planEditor.digitize.retry')}
                        </Button>
                    )}
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-[11px]"
                        data-testid="plan-editor-digitize-dismiss"
                        onClick={digitize.dismiss}
                    >
                        {t('planEditor.dismiss')}
                    </Button>
                </div>
            )}

            {importNotice && (
                <div
                    className={`flex items-center gap-2 border-b border-border px-3 py-2 text-xs ${importNotice.error ? 'text-destructive' : 'text-muted-foreground'}`}
                    role="status"
                    data-testid={importNotice.error ? 'plan-editor-import-error' : 'plan-editor-import-summary'}
                >
                    <AppIcon path={importNotice.error ? mdiAlertCircleOutline : mdiCheckCircleOutline} size="sm" />
                    <span className="flex-1">{importNotice.message}</span>
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-[11px]"
                        data-testid="plan-editor-import-dismiss"
                        onClick={() => setImportNotice(null)}
                    >
                        {t('planEditor.dismiss')}
                    </Button>
                </div>
            )}

            {planProposals.proposals.length > 0 && (
                <div
                    className="flex items-center gap-2 border-b border-border px-3 py-2 text-xs text-muted-foreground"
                    role="status"
                    data-testid="plan-editor-proposals-banner"
                >
                    <AppIcon path={mdiAlertCircleOutline} size="sm" />
                    <span className="flex-1">
                        {t('planEditor.proposals.banner', { count: planProposals.proposals.length })}
                    </span>
                    <Button
                        variant="outline"
                        size="sm"
                        className="h-6 px-2 text-[11px]"
                        data-testid="plan-editor-proposals-open"
                        onClick={() => setProposalsOpen(true)}
                    >
                        {t('planEditor.proposals.open')}
                    </Button>
                </div>
            )}

            <div className="flex min-h-0 flex-1">
                <aside className="hidden w-60 shrink-0 border-r border-border lg:block">
                    {docState && (
                        <LayerPanel
                            doc={docState.doc}
                            version={docState.version}
                            typeLabelOf={localizedTypeLabel}
                            layers={layers}
                            onLayerChange={onLayerChange}
                            onUnderlayChange={onUnderlayChange}
                            selectedKey={selectedKey}
                            selectedKeys={selectedKeys}
                            onSelect={selectAndPan}
                            unregisteredUnderlays={unregisteredUnderlays}
                            onUseAsUnderlay={onUseAsUnderlay}
                            registeringUnderlay={registerUnderlay.isPending}
                            selectedLayerUid={selectedLayerUid}
                            onSelectLayer={onSelectLayer}
                            onCalibrateLayer={onCalibrateLayer}
                            hiddenNetworks={hiddenNetworks}
                            onToggleNetworks={onToggleNetworks}
                            networkLabelOf={networkLabelOf}
                            onUnderlayDiscipline={(repUid, discipline) => { void onUnderlayDiscipline(repUid, discipline) }}
                            residualByRegistration={residualByRegistration}
                            formatDocumentDate={(date) => formatDate(date)}
                        />
                    )}
                </aside>

                <main
                    className="relative min-w-0 flex-1"
                    onDragOver={(e) => {
                        if (e.dataTransfer.types.includes('Files')) e.preventDefault()
                    }}
                    onDrop={(e) => {
                        const file = e.dataTransfer.files?.[0]
                        if (!file) return
                        e.preventDefault()
                        void onImportFile(file)
                    }}
                >
                    <svg
                        ref={attachStage}
                        className={`plan-editor-stage h-full w-full tool-${tool}`}
                        data-testid="plan-editor-stage"
                    >
                        <g data-world>
                            {LAYER_IDS.map((id) => (
                                <g key={id} data-layer={id} className={`plan-layer-${id}`} />
                            ))}
                        </g>
                        <g data-hud />
                    </svg>
                    {sceneQuery.isLoading && (
                        <div className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground" data-testid="plan-editor-loading">
                            {t('planEditor.loading')}
                        </div>
                    )}
                    {sceneQuery.isError && (
                        <div className="absolute inset-0 flex items-center justify-center text-sm text-destructive" data-testid="plan-editor-error">
                            {t('planEditor.load_error')}
                        </div>
                    )}
                    <div className="pointer-events-none absolute bottom-2 left-2 rounded bg-background/80 px-2 py-1 text-[11px] text-muted-foreground" data-testid="plan-editor-hint">
                        {t(`planEditor.hints.${tool}`)}
                    </div>
                    {saving && progress.total > 0 && (
                        <div className="absolute right-2 top-2 rounded bg-background/90 px-2 py-1 text-[11px] text-muted-foreground" data-testid="plan-editor-progress">
                            {t('planEditor.save_progress', { done: progress.done, total: progress.total })}
                        </div>
                    )}
                </main>

                <aside className="hidden w-72 shrink-0 flex-col border-l border-border lg:flex">
                    <div className="min-h-0 flex-1 overflow-y-auto">
                        {/*
                          * Two panels, one slot. With a creation tool armed and
                          * nothing selected, the panel describes what the next
                          * click will make; the moment something is placed or
                          * picked, the inspector takes over and describes that.
                          *
                          * The hand has a third, one-line panel: there is
                          * nothing to describe, and the reason the inspector is
                          * empty is worth saying out loud.
                          */}
                        {tool === 'pan' ? (
                            <div className="p-3 text-xs text-muted-foreground" data-testid="plan-editor-explore-note">
                                <h2 className="pb-1 text-sm font-semibold text-foreground">
                                    {t('planEditor.tools.pan')}
                                </h2>
                                {t('planEditor.explore_hint')}
                            </div>
                        ) : CREATION_TOOLS.includes(tool) && !selectedElement ? (
                            <ToolOptionsPanel
                                tool={tool}
                                org={org}
                                thickness={wallThickness.thickness}
                                recentThicknesses={wallThickness.recent}
                                onThicknessChange={applyWallThickness}
                                equipmentType={armedEquipment}
                equipmentSize={armedSize}
                uncalibrated={frameMmPerUnit === null}
                onCalibrate={onCalibrateShortcut}
                recentSizes={equipmentSizes.recentFor(armedEquipment?.code)}
                onEquipmentSizeChange={onArmEquipmentSize}
                                onEquipmentTypeChange={onArmEquipmentType}
                                pickerOpen={armedPickerOpen}
                                onPickerOpenChange={setArmedPickerOpen}
                                parents={drawParents}
                                parentUid={drawParentUid}
                                onParentChange={setDrawParentUid}
                                networks={networks}
                                runNetwork={armedNetwork ?? networks[0]?.code ?? null}
                                onRunNetworkChange={setArmedNetwork}
                                runWidthMm={armedRunWidth}
                                onRunWidthChange={setArmedRunWidth}
                                runLevel={armedRunLevel}
                                runLevelParents={levelParents}
                                onRunLevelChange={(level) => {
                                    if (armedNetworkCode) rememberRunLevel(armedNetworkCode, level)
                                }}
                                networkLabelOf={networkLabelOf}
                                riserNetwork={armedRiserNetworkCode ?? null}
                                onRiserNetworkChange={setArmedRiserNetwork}
                                riserDiameterMm={armedRiserDiameter}
                                onRiserDiameterChange={setArmedRiserDiameter}
                                riserPassage={armedRiserPassage}
                                onRiserPassageChange={setArmedRiserPassage}
                            />
                        ) : (
                        <InspectorPanel
                            org={org}
                            scopeUid={linkScopeUid}
                            sharedAncestorUids={sharedAncestorUids}
                            scopeLabel={assetIndex.get(linkScopeUid)?.name ?? asset?.name ?? undefined}
                            element={selectedElement}
                            networks={networks}
                            networkLabelOf={networkLabelOf}
                            networkAssetTypeCodes={networkAssetTypeCodes}
                            networkScopeUid={networkScopeUid}
                            networkScopeLabel={networkScopeUid ? assetIndex.get(networkScopeUid)?.name : undefined}
                            runLevelParents={levelParents}
                            runLevelOf={runLevelOfRun}
                            runSegmentAssemblyOf={runSegmentAssemblyOf}
                            runAssemblyNameOf={runAssemblyNameOf}
                            riserAlignmentFor={riserAlignmentFor}
                            onNetworkAssemblyChanged={() => { void networkAssetsQuery.refetch() }}
                            runTargetOf={runTargetOfRun}
                            onRunLevelPicked={rememberRunLevel}
                            openingTypes={openingTypes}
                            roomTypes={roomTypes}
                            roomNumberTypes={roomNumberTypes}
                            nextRoomNumber={nextRoomNumber}
                            onUpdate={onUpdateSelected}
                            onDelete={onDeleteSelected}
                            onDuplicate={onDuplicateSelected}
                            onLinkAsset={onLinkAsset}
                            onPickEquipmentType={onPickEquipmentType}
                            typeLabelOf={localizedTypeLabel}
                            pickerOpen={pickerOpen}
                            onPickerOpenChange={setPickerOpen}
                            promotable={promotable}
                            onPromote={onPromote}
                            promoteAllCount={promoteAllPlan ? promoteAllPlan.spaces + promoteAllPlan.openings : 0}
                            onPromoteAll={() => setPromoteAllOpen(true)}
                            provenance={selectedProvenance}
                            demoteTargets={demoteTargets}
                            onDemote={onDemote}
                            wallOwners={drawParents.map((target) => ({
                                uid: target.uid,
                                name: target.name,
                                crossFrame: needsExplicitRegistration(target.uid, assetIndex, assetUid),
                            }))}
                            onRetargetWall={onRetargetWall}
                            onWallThickness={onSelectedWallThickness}
                            recentThicknesses={wallThickness.recent}
                            selectionCount={selectionCount}
                            mixedTypes={mixedTypes}
                            onUpdatePrimary={onUpdatePrimary}
                            recentSizes={equipmentSizes.recentFor(
                                selectedElement?.kind === 'equipment' ? selectedElement.typeCode : undefined,
                            )}
                            onEquipmentSize={onSelectedEquipmentSize}
                            onEquipmentSizePreset={onSelectedEquipmentSizePreset}
                            grouperOptions={grouperOptionsWithPending}
                            recentGrouperUids={recentGrouperUids}
                            grouperError={selectedGrouperError}
                            grouperCodeError={codeError}
                            focusNameNonce={renameNonce}
                            onSaveRequested={() => { void onSave() }}
                            onAssignGrouper={onAssignGrouper}
                            onCreateGrouper={onCreateGrouper}
                            parentSuggestion={parentSuggestion}
                            onAcceptParentSuggestion={() => {
                                if (parentSuggestion?.parentUid) onParentChange(parentSuggestion.parentUid)
                                else if (parentSuggestion?.parentKey) onParentKeyChange(parentSuggestion.parentKey)
                                setParentSuggestion(null)
                            }}
                            onDismissParentSuggestion={() => setParentSuggestion(null)}
                            mmPerUnit={frameMmPerUnit}
                            toMm={unitsToMm}
                            toUnits={mmToUnits}
                            elementIssues={selectedKey ? floorplanIssuesByKey.get(selectedKey) : undefined}
                            promoteAllBlocked={floorplanBlocked}
                            sharedLayers={selectedSharedLayers}
                            sharedLoading={sharedLoading}
                            onOpenLayer={onOpenLayer}
                            parent={selectedParent}
                            onParentChange={onParentChange}
                        />
                        )}
                    </div>
                    <ValidationPanel issues={allIssues} onSelect={selectAndPan} onPromote={onPromoteIssue} />
                </aside>
            </div>

            {summary && (
                <div
                    className="flex flex-wrap items-start gap-2 border-t border-border bg-muted/40 px-3 py-2 text-xs"
                    data-testid="plan-editor-save-summary"
                    role="status"
                >
                    <AppIcon
                        path={summary.failures.length ? mdiAlertCircleOutline : mdiCheckCircleOutline}
                        size="sm"
                        className={summary.failures.length ? 'text-destructive' : 'text-muted-foreground'}
                    />
                    <div className="flex-1 space-y-1">
                        <div className="text-foreground">
                            {t('planEditor.save_summary', {
                                assets: summary.counts.assets,
                                created: summary.counts.reps,
                                updated: summary.counts.updates,
                                deleted: summary.counts.deletes,
                            })}
                            {summary.counts.registrations > 0 && (
                                <span data-testid="plan-editor-save-registrations">
                                    {' · '}{t('planEditor.save_registrations', { count: summary.counts.registrations })}
                                </span>
                            )}
                            {summary.counts.reparents > 0 && (
                                <span data-testid="plan-editor-save-reparents">
                                    {' · '}{t('planEditor.save_reparents', { count: summary.counts.reparents })}
                                </span>
                            )}
                            {summary.counts.assetUpdates > 0 && (
                                <span data-testid="plan-editor-save-asset-updates">
                                    {' · '}{t('planEditor.save_asset_updates', { count: summary.counts.assetUpdates })}
                                </span>
                            )}
                            {summary.counts.relations > 0 && (
                                <span data-testid="plan-editor-save-relations">
                                    {' · '}{t('planEditor.save_relations', { count: summary.counts.relations })}
                                </span>
                            )}
                            {summary.calibrationWritten && (
                                <span data-testid="plan-editor-save-calibration">
                                    {' · '}{t('planEditor.save_calibration')}
                                </span>
                            )}
                        </div>
                        {summary.orphanedAssets.length > 0 && (
                            <div className="text-muted-foreground" data-testid="plan-editor-orphaned-assets">
                                {t('planEditor.orphaned_assets', { count: summary.orphanedAssets.length })}
                            </div>
                        )}
                        {summary.failures.map((failure, i) => (
                            <div key={`${failure.kind}-${i}`} className="text-destructive" data-testid="plan-editor-save-failure">
                                {t('planEditor.save_failure', { label: failure.label, message: failure.message })}
                            </div>
                        ))}
                    </div>
                    <div className="flex items-center gap-1">
                        {summary.failures.length > 0 && (
                            <Button variant="outline" size="sm" data-testid="plan-editor-save-retry" onClick={onSave}>
                                {t('planEditor.retry')}
                            </Button>
                        )}
                        <Button variant="ghost" size="sm" data-testid="plan-editor-save-summary-dismiss" onClick={() => setSummary(null)}>
                            {t('planEditor.dismiss')}
                        </Button>
                    </div>
                </div>
            )}

            {/* The save was refused: an asset has nowhere legal to be filed, so
              * posting it would lose the drawing to a 422 (spec 2026-09-18-01).
              * Inline and dismissible, like the save summary above it — never a
              * toast. */}
            {refusedParents.length > 0 && (
                <div
                    className="absolute bottom-4 left-1/2 z-20 flex max-w-xl -translate-x-1/2 items-start gap-3 rounded-md border border-destructive bg-card px-3 py-2 text-sm text-destructive shadow-lg"
                    data-testid="plan-editor-parent-refused"
                >
                    <div className="flex-1">
                        {t('planEditor.parent_refused_save', {
                            count: refusedParents.length,
                            names: refusedParents.map((a) => a.name || a.typeCode).join(', '),
                        })}
                    </div>
                    <Button
                        variant="ghost"
                        size="sm"
                        data-testid="plan-editor-parent-refused-dismiss"
                        onClick={() => setRefusedParents([])}
                    >
                        {t('planEditor.dismiss')}
                    </Button>
                </div>
            )}

            {/* The measure tool asks for the real distance — a dialog, never a
              * native prompt (repo UI rules). */}
            <AlertDialog open={measureRequest !== null}>
                <AlertDialogContent data-testid="plan-editor-measure-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('planEditor.measure_title')}</AlertDialogTitle>
                        <AlertDialogDescription>{t('planEditor.measure_description')}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <Input
                        type="number"
                        min={1}
                        step={10}
                        autoFocus
                        value={measureDraft}
                        aria-label={t('planEditor.measure_title')}
                        data-testid="plan-editor-measure-input"
                        onChange={(e) => setMeasureDraft(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key !== 'Enter') return
                            e.preventDefault()
                            onMeasureConfirm()
                        }}
                    />
                    <AlertDialogFooter>
                        <AlertDialogCancel
                            data-testid="plan-editor-measure-cancel"
                            onClick={() => setMeasureRequest(null)}
                        >
                            {t('planEditor.cancel')}
                        </AlertDialogCancel>
                        <AlertDialogAction data-testid="plan-editor-measure-confirm" onClick={onMeasureConfirm}>
                            {t('planEditor.measure_apply')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* §4: auto-calibration is deliberate too — it costs a model call
              * and it overwrites the frame's scale. */}
            <AlertDialog open={autoCalibrateOpen}>
                <AlertDialogContent data-testid="plan-editor-autocalibrate-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('planEditor.autoCalibrate.dialog_title')}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('planEditor.autoCalibrate.dialog_body', {
                                document: primaryScan?.title ?? '',
                                page: primaryScan?.page ?? 1,
                            })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <div className="space-y-2 text-xs text-muted-foreground">
                        <p>{t('planEditor.autoCalibrate.dialog_method')}</p>
                        <p data-testid="plan-editor-autocalibrate-beta-note">{t('planEditor.beta.dialog_note')}</p>
                        {frameMmPerUnit !== null && (
                            <p className="text-foreground" data-testid="plan-editor-autocalibrate-overwrite">
                                {t('planEditor.autoCalibrate.dialog_overwrite', {
                                    value: frameMmPerUnit.toFixed(2),
                                })}
                            </p>
                        )}
                    </div>
                    <AlertDialogFooter>
                        <AlertDialogCancel
                            data-testid="plan-editor-autocalibrate-cancel"
                            onClick={() => setAutoCalibrateOpen(false)}
                        >
                            {t('planEditor.digitize.cancel')}
                        </AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="plan-editor-autocalibrate-confirm"
                            onClick={() => { void onAutoCalibrateConfirm() }}
                        >
                            {t('planEditor.autoCalibrate.confirm')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* §F1: digitizing is deliberate — it costs a model call and
              * produces geometry somebody has to review. */}
            <AlertDialog key={digitizeDialogKey} open={digitizeOpen}>
                <AlertDialogContent data-testid="plan-editor-digitize-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('planEditor.digitize.dialog_title')}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('planEditor.digitize.dialog_body', {
                                document: primaryScan?.title ?? '',
                                page: primaryScan?.page ?? 1,
                            })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <div className="space-y-2 text-xs text-muted-foreground">
                        <p>{t('planEditor.digitize.dialog_duration')}</p>
                        <p>{t('planEditor.digitize.dialog_review')}</p>
                        <p data-testid="plan-editor-digitize-beta-note">{t('planEditor.beta.dialog_note')}</p>
                        {/*
                          * §2: on an uncalibrated frame the dialog is not a
                          * dead end — it shows both ways out and keeps confirm
                          * off until the scene reports a scale.
                          */}
                        {frameMmPerUnit === null && (
                            <div
                                className="rounded-md border border-border p-2"
                                data-testid="plan-editor-digitize-calibration-required"
                            >
                                <p className="font-medium text-foreground">
                                    {t('planEditor.digitize.calibration_required')}
                                </p>
                                <p>{t('planEditor.digitize.calibration_required_hint')}</p>
                                <div className="mt-2 flex gap-2">
                                    <Button
                                        variant="default"
                                        size="sm"
                                        className="h-7 text-[11px]"
                                        data-testid="plan-editor-digitize-autocalibrate"
                                        disabled={!!autoCalibrateDisabledReason}
                                        title={autoCalibrateDisabledReason ?? undefined}
                                        onClick={() => { void onAutoCalibrateConfirm() }}
                                    >
                                        {t('planEditor.autoCalibrate.action')}
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="h-7 text-[11px]"
                                        data-testid="plan-editor-digitize-calibrate-manually"
                                        onClick={() => {
                                            setDigitizeOpen(false)
                                            onCalibrateShortcut()
                                        }}
                                    >
                                        {t('planEditor.autoCalibrate.manual')}
                                    </Button>
                                </div>
                            </div>
                        )}
                        {calibrationUntrusted && (
                            <div
                                className="rounded-md border border-border p-2"
                                data-testid="plan-editor-digitize-calibration-untrusted"
                            >
                                <p className="font-medium text-foreground">
                                    {t('planEditor.digitize.calibration_untrusted')}
                                </p>
                                <p>{t('planEditor.digitize.calibration_untrusted_hint')}</p>
                                <div className="mt-2 flex gap-2">
                                    <Button
                                        variant="default"
                                        size="sm"
                                        className="h-7 text-[11px]"
                                        data-testid="plan-editor-digitize-untrusted-autocalibrate"
                                        disabled={!!autoCalibrateDisabledReason}
                                        title={autoCalibrateDisabledReason ?? undefined}
                                        onClick={() => { void onAutoCalibrateConfirm() }}
                                    >
                                        {t('planEditor.autoCalibrate.action')}
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="h-7 text-[11px]"
                                        data-testid="plan-editor-digitize-untrusted-calibrate-manually"
                                        onClick={() => {
                                            setDigitizeOpen(false)
                                            onCalibrateShortcut()
                                        }}
                                    >
                                        {t('planEditor.autoCalibrate.manual')}
                                    </Button>
                                </div>
                            </div>
                        )}
                        {hasPreviousExtraction && (
                            <div className="rounded-md border border-border p-2">
                                <label className="flex items-start gap-2">
                                    <Checkbox
                                        checked={digitizeReplace}
                                        data-testid="plan-editor-digitize-replace"
                                        onCheckedChange={(v) => setDigitizeReplace(v === true)}
                                    />
                                    <span>
                                        <span className="block font-medium text-foreground">
                                            {t('planEditor.digitize.replace_label')}
                                        </span>
                                        {t('planEditor.digitize.replace_hint')}
                                        {!digitizeReplace && (
                                            <span className="mt-1 block text-destructive">
                                                {t('planEditor.digitize.replace_required')}
                                            </span>
                                        )}
                                    </span>
                                </label>
                            </div>
                        )}
                    </div>
                    <AlertDialogFooter>
                        <AlertDialogCancel
                            data-testid="plan-editor-digitize-cancel"
                            onClick={() => setDigitizeOpen(false)}
                        >
                            {t('planEditor.digitize.cancel')}
                        </AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="plan-editor-digitize-confirm"
                            disabled={(hasPreviousExtraction && !digitizeReplace) || frameMmPerUnit === null || calibrationUntrusted}
                            onClick={() => { void onDigitizeConfirm() }}
                        >
                            {t('planEditor.digitize.confirm')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <DigitizePreviewDialog
                output={digitizePreview?.output ?? null}
                busy={acceptDigitize.isPending}
                error={digitizePreviewError}
                onAccept={(keep) => { void onDigitizePreviewAccept(keep) }}
                onDiscard={onDigitizePreviewDiscard}
            />

            <PlanProposalDialog
                org={org}
                proposals={planProposals.proposals}
                open={proposalsOpen && planProposals.proposals.length > 0}
                onClose={() => setProposalsOpen(false)}
                onDecided={onProposalDecided}
                layerLabelOf={underlayLabelOf}
            />

            {/* §F5: one save, every room and opening of the bundle. */}
            <AlertDialog open={promoteAllOpen && promoteAllPlan !== null}>
                <AlertDialogContent data-testid="plan-editor-promote-all-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('planEditor.promoteAll.dialog_title')}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('planEditor.promoteAll.dialog_body', {
                                spaces: promoteAllPlan?.spaces ?? 0,
                                openings: promoteAllPlan?.openings ?? 0,
                                matched: promoteAllPlan?.matched ?? 0,
                            })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <div className="space-y-1 text-xs text-muted-foreground">
                        {(promoteAllPlan?.walls ?? 0) > 0 && <p>{t('planEditor.promoteAll.dialog_walls')}</p>}
                        {(promoteAllPlan?.equipments ?? 0) > 0 && (
                            <p>{t('planEditor.promoteAll.dialog_equipments', { count: promoteAllPlan?.equipments ?? 0 })}</p>
                        )}
                    </div>
                    <AlertDialogFooter>
                        <AlertDialogCancel
                            data-testid="plan-editor-promote-all-cancel"
                            onClick={() => setPromoteAllOpen(false)}
                        >
                            {t('planEditor.promoteAll.cancel')}
                        </AlertDialogCancel>
                        <AlertDialogAction data-testid="plan-editor-promote-all-confirm" onClick={onPromoteAll}>
                            {t('planEditor.promoteAll.confirm')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={pendingLeave !== null}>
                <AlertDialogContent data-testid="plan-editor-leave-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('planEditor.leave_title')}</AlertDialogTitle>
                        <AlertDialogDescription>{t('planEditor.leave_description')}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel
                            data-testid="plan-editor-leave-cancel"
                            onClick={() => {
                                pendingLeave?.reset()
                                setPendingLeave(null)
                            }}
                        >
                            {t('planEditor.leave_cancel')}
                        </AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="plan-editor-leave-confirm"
                            onClick={() => {
                                pendingLeave?.proceed()
                                setPendingLeave(null)
                            }}
                        >
                            {t('planEditor.leave_confirm')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    )
}

export default PlanEditorPage
